import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useConfirm, showDialog } from '../../components/confirm';
import { FormTitle } from '../../components/FormTitle';
import { AppToolbar } from '../../components/AppToolbar/AppToolbar';
import { ExportMenu } from '../../components/AppToolbar/ExportMenu';
import { buildSourceOptions, reconcileSources } from './dupSpikeSource';
import { fmtLocalDate, fmtLocalShort } from '../../utils/datetime';
// Shared column model + header menu (client 2026-08-05): the NON-frozen data
// columns of the QC-sequence grid get column RESIZE + right-click Hide / Show All /
// Column Chooser (no reorder/sort — sorting would scramble the drag-defined run
// order; the 9 frozen columns stay pinned).
import { useColumnManager, useHeaderMenu } from '../../components/AppDataGrid';
import {
  qcBatchApi,
  type QcJob, type QcInstrument, type QcTemplate, type QcPrepBatch, type QcTypeOption, type QcSample, type QcSeqRow,
  type QcLinkedReagent, type QcAvailableReagent, type QcBatchHeader, type QcPreviousBatch,
  type QcPickMatrix, type QcPickTest,
  type QcCreateResult, type QcCreateBody,
} from './qcBatch.api';
import { WorkflowStepperBar, WORKFLOW_STEPS as BASE_STEPS, type StepState } from './WorkflowStepperBar';
// SDMS Result Entry screen (QC Batch → "Open SDMS"; prototype SCREEN 3).
import SdmsResultEntryPage, { type SdmsRun, type SdmsEntrySeed } from './SdmsResultEntryPage';
import './qcBatch.css';

// Island steps for the PICK-MODE entry (QC Batches list → New, no seeded test):
// the "Select test · New" head is replaced by the in-form cascade the user
// actually walks — Select matrix → Test → Method — then the shared tail
// (Job ID onward) continues identically. The Analysis-Queue entry keeps the
// default WORKFLOW_STEPS. (User request 2026-07-08: the island must mirror
// the actual entry flow.)
const PICK_STEPS = [
  'Select matrix', 'Test', 'Method', 'Job ID', 'Instrument', 'Load', 'Mark samples', 'Sort', 'Others', 'Open SDMS',
] as const;

// ════════════════════════════════════════════════════════════════════════════
// QC Batch detail screen — opened from an Analysis Queue row.  Ports the legacy
// ALPACA QCBatchController read paths: real Job / Instrument / Prep-Batch
// pickers, real Load (pending SampleParameter samples for the test) and real
// Sort (the SDMS Sequence-Pattern interleave, computed server-side by
// QcSequenceBuilder).  Test / Method / Matrix / Template come from the clicked
// queue row.  Create / Rollback / reagent-instrument land in later phases.
// ════════════════════════════════════════════════════════════════════════════

/** Subset of the Analysis Queue row this screen needs (mirrors AnalysisQueueRow). */
export interface QcBatchSeed {
  // Null = the OG "New from the QC Batches list" flow (NewObjectAction_Executing
  // :2647): the form opens with NO test and the user picks Matrix → Test →
  // Method in-form (cascading pickers). Set = Analysis-Queue entry (test known).
  testMethodId: string | null;
  test:         string | null;
  method:       string | null;
  matrix:       string | null;
  templateID:   number | null;
  // Set when arriving from the Sample Details popup's OK-with-a-selection
  // (legacy OG parity — QCBatchController.cs Dc_Accepting ~line 2788, which
  // stores the selection in Qcbatchinfo.lstSamples). Job ID pre-fills from the
  // DISTINCT job(s) the selected samples belong to (multi-job supported, same
  // as legacy's `string.Join(";", ...)` — our jobOids array does this
  // natively); Instrument is still picked manually (legacy doesn't skip it
  // either). preSelectedSampleLogInOids narrows whatever Load returns for
  // those jobs down to exactly the samples that were checked, same as
  // legacy's `.Where(a => a.JobID.JobID == id)` per-job filter.
  preSelectedJobOids?: string[];
  preSelectedSampleLogInOids?: string[];
  // "Open an existing QC batch" (view/edit mode) — set instead of the manual
  // Job ID + Load flow above. testMethodId/test/method/matrix/templateID are
  // NOT known yet at this point (the qc-batches list row doesn't carry
  // TestMethodId — only the display Test name string), so the caller passes
  // placeholders; the detail fetch (keyed on existingAbOid) resolves the real
  // values and overwrites local state once it loads.
  existingAbOid?: string;
  // Row-click opens read-only (legacy `isview=true`, QCBatchController.cs
  // :12079-12093); an explicit Edit affordance flips this off (`isview=false`,
  // legacy EditAction_Executing :2549-2597).
  viewOnly?: boolean;
  // Arriving from a Sample Prep Batch's "Export To QC Batch" (OG
  // SamplePreparationController.btnExportToQCBatch_Execute → opens the QC Batch
  // form seeded with this prep batch). The prep batch's DISPLAY id is used to
  // auto-select it in the Prep Batch combo once the prep list loads, importing
  // its prepared samples exactly as if the user picked it. testMethodId is the
  // prep batch's test method. Mutually exclusive with existingAbOid.
  preSelectedPrepBatchId?: string;
  // Set by the QC Batches LIST "SDMS" toolbar button: open this existing batch
  // (existingAbOid) and, once its detail has loaded, immediately swap into the
  // full-page SDMS sheet — same sheet the detail-view SDMS button opens. Back
  // from the sheet returns straight to the list (not the intermediate detail).
  autoOpenSdms?: boolean;
}

interface Props {
  seed:   QcBatchSeed;
  onBack: () => void;
  /** Set when this form was opened FROM the SDMS screen (SDMS Enter-mode Retrieve
   *  with no batch picked — OG SpreadsheetEntryController :5507 pops the
   *  `QCBatchsequence` dashboard for exactly this). Instead of swapping in our own
   *  full-page SDMS (which would nest a second SDMS inside the popup), hand the
   *  built seed back to the SDMS screen that opened us and let IT take over. */
  onSdmsSeed?: (seed: SdmsEntrySeed) => void;
  /** Hosted inside a dialog (the SDMS "New QC Batch" popup) rather than owning the
   *  page. Suppresses this form's PAGE-level chrome, which either duplicates or
   *  contradicts the dialog around it:
   *    • the docked workflow stepper — it portals into the AppShell topbar, so in a
   *      popup it hijacks the whole app header (and describes the Analysis-Queue
   *      walk, which this user is not on);
   *    • the "Step 3. Pick Job ID + Instrument…" hint strip — page guidance the
   *      dialog's own title/steps already cover;
   *    • the "Analysis Queue / QC Batch" breadcrumb — plainly wrong here, the user
   *      came from SDMS.
   *  The form's own toolbar, title (which carries the audit chip) and body are
   *  unchanged. */
  embedded?: boolean;
}

// ── row model for the batch sequence ────────────────────────────────────────
type RowKind = 'sample' | 'qc' | 'dilution';
interface SeqRow {
  // `sort` is null PRE-Sort: after Load the cell is BLANK and only fills in
  // when the user ticks the row's checkbox (auto-incremental, click order;
  // unticking renumbers the remaining checked rows so values stay
  // contiguous).  Post-Sort the server-built sequence carries numeric values
  // for every row (Sample + QC + dilution).
  id: string; kind: RowKind; qc: string; sort: number | null; checked: boolean;
  // The persisted QCBatchSequence.Oid when this row came from a SAVED batch
  // (undefined for a freshly-built or freshly-added row) — round-tripped so the
  // reconcile-on-Save update (A1) can diff existing vs added rows.
  seqOid?: string | null;
  code: string; job: string; component: string; sid: string;
  // `dilutionCount` = the pre-Sort editable "DilutionCount" column (uint,
  // default 1, correlated to No. Runs — legacy QCBatchController line 2170).
  // `df` = the post-Sort "DF" column (the legacy `Dilution` factor, default 1).
  // They are DISTINCT columns (Model.xafml QCBatchSequence ListView: Dilution
  // caption "DF" AND DilutionCount caption "DilutionCount").
  dilutionCount: number;
  df: number; sampleAmount: string; finalVol: string;
  dupSource: string; spikeSource: string;
  sourceId?: string; dil?: number;
  // Dilution provenance + reported-run flag — carried for persistence only
  // (legacy QCBatchSequence.OriginalSYSSamplecode / IsReport), not rendered.
  originalSysSampleCode: string | null; isReport: boolean;
  // Legacy QC Batch grid columns (read-only display unless editable per
  // user requirement: dilutionCount + finalVol(=FinalVolMl) + excludedReport
  // are editable; SampleAmount is editable + client-only).
  excludedReport: boolean;
  units: string;
  sampleName: string;
  siteName: string;
  samplingAddress: string;
  dateCreated: string | null;
  dateReceived: string | null;
  dateCollected: string | null;
  client: string;
  projectId: string;
  projectName: string;
  holdTime: string;
  // carried for persistence (Create)
  qcTypeOid?: string | null; sampleLogInOid?: string | null;
  systemId?: string; strSampleId?: string; runno?: number;
}
let uidN = 0;
const uid = () => 'r' + (++uidN);

// Every QC Batch form acknowledgement uses the shared centered Tier-1 dialog
// (user request 2026-07-11), never the transient top banner — so instrument,
// reagent, rollback, notes, copy, etc. all render in the one global style.
// (error/warning already auto-route to the modal via useNotify.)
// Routine acknowledgement shown as a centered modal dialog (user preference
// 2026-07-16) via the shared showDialog. (error/warning already auto-route to the
// modal via useNotify.)
const qcMsg = (message: string, title = 'QC Batch', type: 'success' | 'info' = 'success') =>
  void showDialog({ type, title, message });

const seqRowToLocal = (r: QcSeqRow): SeqRow => ({
  id: uid(), kind: r.kind, qc: r.qcType, sort: r.sort, checked: false,
  seqOid: r.seqOid ?? null,
  code: r.code, job: r.jobId ?? '', component: r.component ?? '',
  // SampleID column binds the SAMPLE LINK only (legacy QCBatchSequence.SampleID →
  // SampleLogIn.SampleID).  It must stay BLANK on a standalone QC row (Method Blank,
  // CCB, ICV…): the old `?? r.strSampleId` fallback rendered that row's own run code
  // ("Method Blank1") in the SampleID column, which OG leaves empty.  Kept
  // display-side too (not just the builder fix) so batches SAVED with the old
  // StrSampleID value still render OG-correct on reopen. (fix 2026-08-07)
  sid: r.sampleId ?? '',
  // Post-Sort: df is the server dilution factor (DF column). dilutionCount is
  // hidden post-Sort but carried through for parity.
  dilutionCount: r.dilutionCount ?? 1,
  df: r.df, sampleAmount: r.sampleAmount ?? '', finalVol: r.finalVol ?? '',
  dupSource: r.dupSource ?? '', spikeSource: r.spikeSource ?? '',
  sourceId: r.sourceId ?? undefined,
  originalSysSampleCode: r.originalSysSampleCode ?? null, isReport: r.isReport ?? true,
  // Sample / dilution / sample-linked-QC rows carry their source metadata
  // through Sort; pure QC rows (CCB/ICV/LCS…) come back null → render blank.
  excludedReport: r.excludedReport ?? false, units: r.units ?? '',
  sampleName: r.sampleName ?? '', siteName: r.siteName ?? '',
  samplingAddress: r.samplingAddress ?? '',
  dateCreated: r.dateCreated, dateReceived: r.dateReceived, dateCollected: r.dateCollected,
  client: r.client ?? '', projectId: r.projectId ?? '',
  projectName: r.projectName ?? '', holdTime: r.holdTime ?? '',
  qcTypeOid: r.qcTypeOid, sampleLogInOid: r.sampleLogInOid,
  // No `?? r.code` fallback — OG persists SystemID NULL on Sample rows and the
  // SHORT run code on QC rows; falling back to the full code would corrupt both.
  systemId: r.systemId ?? '', strSampleId: r.strSampleId ?? undefined, runno: r.runno,
});

const sampleToLocal = (s: QcSample): SeqRow => ({
  // sort defaults to 0; tick the row's checkbox to assign an incremental (1-based) Sort.
  id: uid(), kind: 'sample', qc: 'Sample', sort: 0, checked: false,
  code: s.sysSampleCode ?? '', job: s.jobID ?? '', component: s.component ?? '',
  // SampleID column = the sample code (SampleLogIn.SampleID), same as
  // SysSampleCode — matches the legacy XAF binding.  (ClientSampleID is the
  // descriptive SampleName, shown in its own column — not here.)
  sid: s.sysSampleCode ?? '',
  // Pre-Sort DilutionCount (editable, correlates to No. Runs); DF defaults to 1.
  dilutionCount: s.dilutionCount > 0 ? s.dilutionCount : 1,
  df: 1,
  sampleAmount: '',
  finalVol: s.finalVolMl ?? '',
  dupSource: '', spikeSource: '',
  originalSysSampleCode: null, isReport: false,
  excludedReport: s.excludedReport,
  units: s.units ?? '',
  sampleName: s.sampleName ?? '',
  siteName: s.siteName ?? '',
  samplingAddress: s.samplingAddress ?? '',
  dateCreated: s.dateCreated, dateReceived: s.dateReceived, dateCollected: s.dateCollected,
  client: s.client ?? '',
  projectId: s.projectID ?? '',
  projectName: s.projectName ?? '',
  holdTime: s.holdTime ?? '',
  qcTypeOid: null, sampleLogInOid: s.sampleLogInOid,
  // SystemID stays empty on Sample rows (OG persists NULL — live-data verified).
  systemId: '', strSampleId: s.sysSampleCode ?? '', runno: 1,
});

// The local grid row → wire shape, shared by Create (onOk), Add QC, and Remove
// QC — all three POST the CURRENT full grid to the server.
const toWireRow = (r: SeqRow): QcSeqRow => ({
  seqOid: r.seqOid ?? null,
  kind: r.kind, qcType: r.qc, qcTypeOid: r.qcTypeOid ?? null,
  sort: r.sort ?? 0, runno: r.runno ?? 1, code: r.code, systemId: r.systemId || null,
  jobId: r.job || null, component: r.component || null,
  strSampleId: r.strSampleId ?? null, sampleId: r.sid || null,
  sampleLogInOid: r.sampleLogInOid ?? null, df: r.df,
  sampleAmount: r.sampleAmount || null, finalVol: r.finalVol || null,
  dupSource: r.dupSource || null, spikeSource: r.spikeSource || null,
  sourceId: r.sourceId ?? null,
  originalSysSampleCode: r.originalSysSampleCode ?? null, isReport: r.isReport ?? true,
  dilutionCount: r.dilutionCount, excludedReport: r.excludedReport,
  units: r.units || null, sampleName: r.sampleName || null,
  siteName: r.siteName || null, samplingAddress: r.samplingAddress || null,
  dateCreated: r.dateCreated, dateReceived: r.dateReceived, dateCollected: r.dateCollected,
  client: r.client || null, projectId: r.projectId || null,
  projectName: r.projectName || null, holdTime: r.holdTime || null,
});

// Signature of the edit-flow SAVABLE state (sequence + Room Temp/Humidity/Comment)
// — what saveEdits sends. Used to gate the OK button: equal to the loaded baseline
// ⇒ nothing changed ⇒ OK disabled. Serialising toWireRow catches every grid edit
// (add/remove QC or sample, dilution, move, field edits) plus the three header
// fields; `id` isn't in the wire shape, so it's stable across renders.
const editSig = (rows: SeqRow[], roomTemp: string, humidity: string, comment: string): string =>
  JSON.stringify({ s: rows.map(toWireRow), rt: roomTemp, h: humidity, c: comment });

export default function QcBatchDetailPage({ seed, onBack, onSdmsSeed, embedded }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { prompt } = useConfirm();

  // ── "open an existing QC batch" (view/edit mode) ──
  // Local, overwritable copies of the seed's Test/Method/Matrix/TestMethodId —
  // for a brand-new batch these equal the seed values immediately (unchanged
  // behavior); for an EXISTING batch (seed.existingAbOid set) they start empty
  // and are filled in once the detail fetch below resolves the header, which
  // in turn (via the dependency array on the reference-data effect further
  // down) triggers that effect to fetch jobs/instruments/templates/prep/
  // qc-types for the NOW-known test method — same effect, no separate gate.
  const [effTestMethodId, setEffTestMethodId] = useState(seed.existingAbOid ? '' : seed.testMethodId ?? '');
  const [effTest, setEffTest]     = useState(seed.existingAbOid ? null : seed.test);
  const [effMethod, setEffMethod] = useState(seed.existingAbOid ? null : seed.method);
  const [effMatrix, setEffMatrix] = useState(seed.existingAbOid ? null : seed.matrix);

  // ── OG "New from the QC Batches list" — cascading Matrix → Test → Method ──
  // Legacy opens the create dashboard with NO test (NewObjectAction_Executing
  // :2647); the user then picks Matrix → Test → Method, each lookup's
  // datasource gated on the previous pick (MatrixDataSource :635 /
  // TestDataSource :680 / MethodDataSource :789). The Method pick is the
  // DEFINITIVE TestMethod — it sets effTestMethodId, which fires the existing
  // reference-data effect (jobs/instruments/templates/prep/qc-types) unchanged.
  const pickMode = !seed.existingAbOid && !seed.testMethodId;
  const [matrixList, setMatrixList] = useState<QcPickMatrix[]>([]);
  const [testList,   setTestList]   = useState<QcPickTest[]>([]);
  const [methodList, setMethodList] = useState<QcPickTest[]>([]);
  const [pickMatrix, setPickMatrix] = useState<QcPickMatrix | null>(null);
  const [pickTest,   setPickTest]   = useState<QcPickTest | null>(null);
  // Row-click on an existing batch opens read-only (legacy isview=true,
  // QCBatchController.cs :12079-12093); the toolbar's "Edit" action
  // (onEdit below) flips it off (legacy EditAction_Executing :2549-2597).
  const [viewOnly, setViewOnly] = useState(seed.viewOnly ?? false);
  const [existingHeader, setExistingHeader] = useState<QcBatchHeader | null>(null);

  // ── reference data (real) ──
  const [jobsList, setJobsList]   = useState<QcJob[]>([]);
  const [instrList, setInstrList] = useState<QcInstrument[]>([]);
  const [tplList, setTplList]     = useState<QcTemplate[]>([]);
  const [prepList, setPrepList]   = useState<QcPrepBatch[]>([]);
  const [qcTypeList, setQcTypeList] = useState<QcTypeOption[]>([]);
  const [loadedSamples, setLoadedSamples] = useState<QcSample[]>([]);

  // ── selections / form state ──
  const [jobOids, setJobOids]       = useState<string[]>(seed.preSelectedJobOids ?? []);     // Samplecheckin.Oid[]
  // Set once (from the Sample Details popup's OK selection) and persists for
  // this whole QC Batch session, same lifetime as legacy's `Qcbatchinfo.
  // lstSamples` — Load applies it every time it runs, not just the first.
  // Cleared on a full Reset (see resetBatch below).
  const [preSelectedSlOids, setPreSelectedSlOids] =
    useState<string[] | null>(seed.preSelectedSampleLogInOids ?? null);
  const [instrument, setInstrument] = useState<string | null>(null);   // instrumentOid
  const [tplId, setTplId]           = useState<number | null>(seed.templateID);    // SDMS TemplateID
  const [prepBatch, setPrepBatch]   = useState('N/A');
  const [loaded, setLoaded]         = useState(false);
  const [sorted, setSorted]         = useState(false);
  const [rows, setRows]             = useState<SeqRow[]>([]);
  const [tab, setTab]               = useState<'seq' | 'others'>('seq');
  const [openPop, setOpenPop]       = useState<'job' | 'instr' | 'tpl' | 'prep' | 'matrix' | 'test' | 'method' | null>(null);
  const [giCollapsed, setGiCollapsed] = useState(false);
  const [qcCollapsed, setQcCollapsed] = useState(false);
  const [fullGrid, setFullGrid]     = useState(false);
  const [noRuns, setNoRuns]         = useState('1');
  const [roomTemp, setRoomTemp]     = useState('');
  const [humidity, setHumidity]     = useState('');
  // Header Comment (Others-tab) — plain text (legacy SpreadSheetEntry_AnalyticalBatch.
  // Comments is a plain nvarchar(max) memo, NOT RTF), persisted with the batch on
  // Create + Save (never a per-action save).
  const [comment,  setComment]      = useState('');
  // Others-tab Instrument list (legacy InstrumentLink_Execute — a DIFFERENT,
  // broader catalog than the single header Instrument field above). Real
  // persisted state, loaded once the batch is saved (see savedBatchId below);
  // was previously a client-only stub that never actually saved anywhere.
  const [instrumentRows, setInstrumentRows] = useState<QcInstrument[]>([]);
  const [instrumentSel, setInstrumentSel]   = useState<Set<string>>(new Set());
  // Instruments staged BEFORE the batch is saved (legacy InstrumentLink's Active
  // flag is gated on user permission only, not on the batch being saved — see
  // ctrl :120-187; only the AUDIT write is skipped for a new/unsaved batch, not
  // the add itself). Persisted atomically with Create; cleared once savedBatchId
  // is set (the grid switches to the real, server-loaded `instrumentRows` above).
  const [stagedInstruments, setStagedInstruments] = useState<QcInstrument[]>([]);
  const [stagedInstrumentSel, setStagedInstrumentSel] = useState<Set<string>>(new Set());
  // Set once Create succeeds (the batch's SpreadSheetEntry_AnalyticalBatch Oid) —
  // gates the saved-only Phase 3 actions (Rollback, Add Samples, …) and feeds
  // the shared AuditTrailButton's auditSourceId.
  const [savedBatchId, setSavedBatchId] = useState<string | null>(null);
  // "Open SDMS" swaps the whole QC detail for the SDMS Result Entry screen.
  const [sdmsOpen, setSdmsOpen] = useState(false);
  // OK→SDMS handoff (OG QCsort "Ok", QCBatchController.cs :7213-7330): an
  // SDMS-template batch is NOT saved in this form — OG hands the built sequence to
  // the SDMS result-entry screen and the batch is saved (NewABID) FROM SDMS. This
  // holds the create payload staged for that SDMS Save. Null for a plain batch.
  const [pendingSdms, setPendingSdms] = useState<QcCreateBody | null>(null);
  // Reagents (Others tab, POST-save — real, server-loaded once savedBatchId is set).
  const [reagentRows, setReagentRows] = useState<QcLinkedReagent[]>([]);
  const [reagentSel, setReagentSel]   = useState<Set<string>>(new Set());
  // Reagents staged BEFORE the batch is saved — legacy parity (ReagentLink's
  // Active flag is gated on user permission only, not on the batch being saved,
  // ctrl :120-187; only the audit write is skipped for a new batch). Persisted
  // atomically with Create.
  const [stagedReagents, setStagedReagents] = useState<QcAvailableReagent[]>([]);
  const [stagedReagentSel, setStagedReagentSel] = useState<Set<string>>(new Set());

  const [modal, setModal] = useState<null | 'notes' | 'copy' | 'instr2' | 'reagent' | 'addsample'>(null);

  // ── QC-sequence grid column model (client 2026-08-05: RESIZE + Hide / Show All /
  // Column Chooser on the NON-frozen data columns only; the 9 frozen leading
  // columns stay pinned; deliberately NO reorder/sort — the run order is
  // drag-defined). visibleColumns drives BOTH the header and body, so a hidden
  // column drops from both; persistKey restores the layout on the next visit. ────
  const QC_DATA_COLS = useMemo(() => [
    { key: 'dilution',        label: sorted ? 'DF' : 'DilutionCount' },
    { key: 'sampleAmount',    label: 'SampleAmount' },
    { key: 'finalVol',        label: 'FinalVol(ml)' },
    { key: 'dupSource',       label: 'DupSource' },
    { key: 'spikeSource',     label: 'SpikeSource' },
    { key: 'sampleName',      label: 'SampleName' },
    { key: 'siteName',        label: 'SiteName' },
    { key: 'samplingAddress', label: 'SamplingAddress' },
    { key: 'units',           label: 'Units' },
    { key: 'holdTime',        label: 'HoldTime' },
    { key: 'dateCreated',     label: 'DateCreated' },
    { key: 'dateReceived',    label: 'DateReceived' },
    { key: 'dateCollected',   label: 'DateCollected' },
    { key: 'client',          label: 'Client' },
    { key: 'projectId',       label: 'ProjectID' },
    { key: 'projectName',     label: 'ProjectName' },
    { key: 'excludedReport',  label: 'ExcludedReport' },
  ], [sorted]);
  const seqCm = useColumnManager(QC_DATA_COLS, c => c.key, c => c.label, 'qcbatch-detail');
  // Header right-click menu → Hide / Show All / Column Chooser (Sort items disabled:
  // the QC run order is drag-defined, not sortable). Portals to body; rendered once.
  const seqMenu = useHeaderMenu({ sort: { key: null, dir: null }, setSortExplicit: () => {} }, seqCm.columnMenu);
  // A user-dragged width overrides the fixed CSS min/max on the narrow data columns
  // (col-num-sm/col-df/col-dupspike) — applied to BOTH header th AND body td so the
  // auto-layout column actually takes it. Empty when the column isn't resized.
  const seqW = (k: string) => {
    const w = seqCm.colWidth(k);
    return w ? { width: w, minWidth: w, maxWidth: w } : {};
  };
  // ── DupSource / SpikeSource selection combos (legacy per-row option map) ─────
  // Shared with the prep form via utils in ./dupSpikeSource — one implementation of
  // the rule (CLAUDE.md rule 8).  Recomputed from the LIVE grid so adding, removing
  // or renumbering rows immediately re-narrows every dropdown.  A row absent from the
  // map has no valid sources and stays plain text (legacy cancels the editor there).
  const sourceOpts = useMemo(
    () => buildSourceOptions(rows, qcTypeList),
    [rows, qcTypeList]);

  // Keep stored Dup/Spike sources valid as rows come and go (user 2026-08-07: pointed
  // DupSource at an added dilution, then removed the dilution). A now-invalid value
  // ADOPTS the only remaining candidate — the computed default, which is what legacy
  // falls back to — or clears when there are several to choose between. Only writes
  // when something actually changes, so it cannot loop.
  useEffect(() => {
    if (!sorted) return;
    const fixes = reconcileSources(rows, sourceOpts);
    if (fixes.length === 0) return;
    const byId = new Map(fixes.map(f => [f.id, f]));
    setRows(prev => prev.map(r => {
      const f = byId.get(r.id);
      return f ? { ...r, ...f, id: r.id } : r;
    }));
  }, [rows, sorted, sourceOpts]);

  // ── "Export to" (legacy XAF default ExportController above the sequence grid) ──
  // Ports OG's grid Export-to dropdown: the SAME 10 ALPACA formats (CSV · HTML ·
  // Image · MHT · Text · PDF · RTF · XLS · XLSX · DOCX) via the ONE shared
  // <ExportMenu> (CLAUDE.md rule 8 — reuse the canonical service, never a second
  // implementation).  The matrix mirrors the grid EXACTLY: the 6 data-bearing
  // frozen columns (the drag / checkbox / remove chrome is not data, so XAF's
  // export omits it too) followed by the CURRENTLY VISIBLE data columns in their
  // on-screen order — so Hide / Column Chooser and the pre/post-Sort caption swap
  // (DilutionCount ⇄ DF) all carry into the file.  Cell text is produced by the
  // same expressions the <td>s render (dates via the shared fmtLocal* helper,
  // §14b.6; dup/spike blank pre-Sort; the ExcludedReport checkbox as Yes/No).
  const seqExportText = (r: SeqRow, key: string): string => {
    switch (key) {
      case 'dilution':       return String((sorted ? r.df : r.dilutionCount) || '');
      case 'sampleAmount':   return r.sampleAmount || '';
      case 'finalVol':       return r.finalVol || '';
      case 'dupSource':      return sorted ? (r.dupSource || '') : '';
      case 'spikeSource':    return sorted ? (r.spikeSource || '') : '';
      case 'dateCreated':    return fmtLocalShort(r.dateCreated);
      case 'dateReceived':   return fmtLocalShort(r.dateReceived);
      case 'dateCollected':  return fmtLocalShort(r.dateCollected);
      case 'excludedReport': return r.excludedReport ? 'Yes' : 'No';
      default:               return String((r as unknown as Record<string, unknown>)[key] ?? '');
    }
  };
  const seqExport = useMemo(() => ({
    headers: ['QCType', 'Sort', 'SysSampleCode', 'JobID', 'Component', 'SampleID',
              ...seqCm.visibleColumns.map(c => c.label)],
    rows: rows.map(r => [
      r.qc, r.sort ?? '', r.code || '', r.job || '', r.component || '', r.sid || '',
      ...seqCm.visibleColumns.map(c => seqExportText(r, c.key)),
    ].map(String)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seqExportText is a pure
    // formatter recreated each render; the data it reads is covered by rows/sorted.
  }), [rows, sorted, seqCm.visibleColumns]);

  // Per-column header CSS class (matches the original fixed <th> classes).
  const seqHClass: Record<string, string> = {
    dilution:      `num ${sorted ? 'col-df' : 'col-num-sm'}`,
    sampleAmount:  'num col-num-sm',
    finalVol:      'num col-num-sm',
    dupSource:     'col-dupspike',
    spikeSource:   'col-dupspike',
    excludedReport:'cbcol',
  };

  // ── edit-flow "dirty" tracking (OK button gating) ──────────────────────────
  // OK is enabled only once the open batch actually changed. `editBaseline` is the
  // signature of the loaded savable state (sequence + Room Temp/Humidity/Comment)
  // captured in loadDetail; `tabsDirty` flags an "other tab" change (reagents/
  // instruments — those persist immediately, but still count as an edit). Both
  // reset on (re)load. (user request 2026-07-11)
  const editBaseline = useRef<string | null>(null);
  const [tabsDirty, setTabsDirty] = useState(false);
  // ── load reference data for the chosen test method ──
  // Keyed on effTestMethodId (not seed.testMethodId directly) so this ALSO
  // fires for an existing-batch open once the detail fetch above resolves the
  // real test method — starts empty (guarded below) for that case, same
  // effect, no separate gate needed.
  useEffect(() => {
    if (!effTestMethodId) return;
    let cancelled = false;
    (async () => {
      try {
        const [j, i, t, p, q] = await Promise.all([
          qcBatchApi.jobs(effTestMethodId),
          qcBatchApi.instruments(effTestMethodId),
          qcBatchApi.templates(effTestMethodId),
          qcBatchApi.prepBatches(effTestMethodId),
          qcBatchApi.qcTypes(effTestMethodId),
        ]);
        if (cancelled) return;
        setJobsList(j); setInstrList(i); setTplList(t); setPrepList(p); setQcTypeList(q);
      } catch {
        if (!cancelled) notify.error('Failed to load QC batch reference data — check the API logs.');
      }
    })();
    return () => { cancelled = true; };
  }, [effTestMethodId, notify]);

  // Export-To-QC-Batch arrival (OG btnExportToQCBatch_Execute): the prep detail
  // hands us its prep batch's display id; once the Prep Batch list has loaded,
  // auto-select it (identical to the user choosing it in the Prep Batch combo)
  // so its prepared samples seed the grid immediately. Runs once.
  const prepAutoImported = useRef(false);
  useEffect(() => {
    if (prepAutoImported.current) return;
    const wantId = seed.preSelectedPrepBatchId;
    if (!wantId || prepList.length === 0) return;
    if (!prepList.some(b => (b.prepBatchID ?? '') === wantId)) return;
    prepAutoImported.current = true;
    void pickPrep(wantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepList, seed.preSelectedPrepBatchId]);

  // Project Notes count for the toolbar caption ("Project Notes (N)") — job-scoped,
  // same feed the NotesModal reads. Loaded when the job changes and refreshed when
  // the notes modal closes. silentLoading so the background fetch never flashes the
  // global loader.
  const [notesCount, setNotesCount] = useState<number | null>(null);
  const loadNotesCount = useCallback(async () => {
    const job = jobOids[0];
    if (!job) { setNotesCount(null); return; }
    try {
      const r = await api.get(`/api/v1/reporting/custom/${encodeURIComponent(job)}/project-notes`,
        { silentLoading: true } as never);
      setNotesCount(((r.data as { data?: unknown[] }).data ?? []).length);
    } catch { setNotesCount(null); }
  }, [jobOids]);
  useEffect(() => { void loadNotesCount(); }, [loadNotesCount]);

  // Pick-mode: load the Matrix options once (the Test/Method lists load on
  // each upstream pick — see onPickMatrix / onPickTest below).
  useEffect(() => {
    if (!pickMode) return;
    let cancelled = false;
    (async () => {
      try {
        const m = await qcBatchApi.newPickerMatrices();
        if (!cancelled) setMatrixList(m);
      } catch {
        if (!cancelled) notify.error('Failed to load the Matrix list — check the API logs.');
      }
    })();
    return () => { cancelled = true; };
  }, [pickMode, notify]);

  // Changing an upstream pick invalidates everything downstream of it —
  // mirrors legacy's gated datasources (a matrix change clears Test+Method;
  // a test change clears Method) and resets any loaded samples/sequence.
  function clearFromMatrix() {
    setPickTest(null); setTestList([]); setMethodList([]);
    setEffTest(null); setEffMethod(null); setEffTestMethodId('');
    setJobOids([]); setInstrument(null); setTplId(null); setPrepBatch('N/A');
    setLoaded(false); setSorted(false); setRows([]); setLoadedSamples([]);
  }
  async function onPickMatrix(m: QcPickMatrix) {
    setPickMatrix(m); setEffMatrix(m.matrixName);
    clearFromMatrix();
    setOpenPop(null);
    try { setTestList(await qcBatchApi.newPickerTests(m.matrixOid)); }
    catch { notify.error('Failed to load the Test list — check the API logs.'); }
  }
  async function onPickTest(t: QcPickTest) {
    setPickTest(t); setEffTest(t.testName);
    setMethodList([]); setEffMethod(null); setEffTestMethodId('');
    setJobOids([]); setInstrument(null); setTplId(null); setPrepBatch('N/A');
    setLoaded(false); setSorted(false); setRows([]); setLoadedSamples([]);
    setOpenPop(null);
    try { setMethodList(await qcBatchApi.newPickerMethods(t.testMethodId)); }
    catch { notify.error('Failed to load the Method list — check the API logs.'); }
  }
  function onPickMethod(m: QcPickTest) {
    // The Method pick IS the definitive TestMethod (legacy MethodDataSource) —
    // setting effTestMethodId fires the reference-data effect above. A re-pick
    // re-scopes jobs/instruments, so clear every downstream selection too.
    setEffMethod(m.methodNumber);
    setEffTestMethodId(m.testMethodId);
    setJobOids([]); setInstrument(null); setTplId(null); setPrepBatch('N/A');
    setLoaded(false); setSorted(false); setRows([]); setLoadedSamples([]);
    setOpenPop(null);
  }

  // Fetch a saved batch's header + built sequence and populate local state.
  // Shared by the initial open (effect below) and the post-Save refresh (A1) so
  // the grid always reflects the freshly-persisted rows (new seqOids for added
  // rows, removed rows gone). Setting effTestMethodId re-fires the reference-data
  // effect above for the now-known test method (same effect, no separate gate).
  const loadDetail = useCallback(async (abOid: string) => {
    const { header, sequence } = await qcBatchApi.getDetail(abOid);
    setExistingHeader(header);
    setEffTestMethodId(header.testMethodId);
    setEffTest(header.test); setEffMethod(header.method); setEffMatrix(header.matrix);
    setTplId(header.templateId);
    setNoRuns(String(header.noRuns ?? 1));
    setRoomTemp(header.roomTemp ?? '');
    setHumidity(header.humidity ?? '');
    setComment(header.comments ?? '');
    setInstrument(header.instrumentOids);
    // Set jobOids DIRECTLY from the header's resolved Samplecheckin.Oids — the
    // picker's jobs() list only holds jobs with PENDING samples, and a saved
    // batch's jobs are already off the queue, so it can't resolve them. This
    // fills the Job ID display AND unblocks Project Notes (needs a real jobOid).
    setJobOids(header.jobOids ? header.jobOids.split(';').filter(Boolean) : []);
    const localRows = sequence.map(seqRowToLocal);
    setRows(localRows);
    setLoaded(true);
    setSorted(true);
    setSavedBatchId(header.abOid);
    // Baseline for the OK dirty-gate: the just-loaded savable state (nothing to
    // save until this signature changes). tabsDirty resets too.
    editBaseline.current = editSig(localRows, header.roomTemp ?? '', header.humidity ?? '', header.comments ?? '');
    setTabsDirty(false);
  }, []);

  // "Open an existing QC batch" (view/edit mode).
  useEffect(() => {
    if (!seed.existingAbOid) return;
    let cancelled = false;
    (async () => {
      try { if (!cancelled) await loadDetail(seed.existingAbOid!); }
      catch { if (!cancelled) notify.error('Failed to load the QC batch — check the API logs.'); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed.existingAbOid]);

  // QC Batches LIST "SDMS" button (seed.autoOpenSdms): once the batch detail has
  // finished loading (rows + header populated), swap straight into the full-page
  // SDMS sheet — the same sheet the detail-view SDMS toolbar button opens. Fires
  // exactly once (a ref guard) so returning from the sheet doesn't re-trigger it.
  const autoSdmsFired = useRef(false);
  useEffect(() => {
    if (seed.autoOpenSdms && loaded && !autoSdmsFired.current) {
      autoSdmsFired.current = true;
      setSdmsOpen(true);
    }
  }, [seed.autoOpenSdms, loaded]);

  // Analysis Queue "View details" → OK: the analyst ticked their samples in the
  // popup, so land them in the grid immediately — same result as clicking Load in
  // this form (legacy QCBatchController Dc_Accepting pre-loads the picked samples
  // into the batch). New batch only (not an existing batch or a prep-batch import);
  // runs once. The instrument is still required before Sort/OK, so this uses
  // force=true to bypass only the Load-button's instrument gate.
  const autoLoadedFromQueue = useRef(false);
  useEffect(() => {
    if (autoLoadedFromQueue.current) return;
    if (seed.existingAbOid || seed.preSelectedPrepBatchId) return;   // existing batch / prep import
    if (!preSelectedSlOids || preSelectedSlOids.length === 0) return;
    if (!effTestMethodId || jobOids.length === 0) return;
    autoLoadedFromQueue.current = true;
    void loadSamples({ force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effTestMethodId, jobOids, preSelectedSlOids, seed.existingAbOid, seed.preSelectedPrepBatchId]);

  // (jobOids for a saved batch are set directly from header.jobOids in
  // loadDetail — no reverse-resolve through the pending-jobs list, which
  // wouldn't contain an already-processed batch's jobs.)

  // ── stepper status ──
  const stepStatus = useMemo<StepState[]>(() => {
    // Shared TAIL — Job ID · Instrument · Load · Mark samples · Sort · Others ·
    // Open SDMS: identical logic for both entry flows; all 'todo' until the
    // test method is resolved.
    const tail: StepState[] = new Array(BASE_STEPS.length - 2).fill('todo');
    if (effTestMethodId) {
      const jobOk = jobOids.length > 0 || prepBatch !== 'N/A';
      tail[0] = jobOk ? 'done' : 'active';
      if (jobOk) tail[1] = instrument ? 'done' : 'active';
      if (jobOk && instrument) tail[2] = loaded ? 'done' : 'active';
      if (loaded) {
        const marked = rows.some(r => r.checked);
        tail[3] = marked ? 'done' : 'active';
        if (marked) tail[4] = sorted ? 'done' : 'active';
      }
      if (sorted) { tail[3] = 'done'; tail[4] = 'done'; tail[6] = 'active'; }
      // "Others" (reagents/instruments) is done once ANYTHING has been added on
      // either tab — saved-batch (instrumentRows/reagentRows) OR pre-save
      // (stagedInstruments/stagedReagents). Previously only checked
      // instrumentRows, so adding a reagent (with no instrument) — or adding
      // either one BEFORE the batch was saved — never advanced this step.
      const hasOthers = instrumentRows.length > 0 || reagentRows.length > 0
        || stagedInstruments.length > 0 || stagedReagents.length > 0;
      if (hasOthers) tail[5] = 'done';
      else if (sorted) tail[5] = 'optional';
    }
    if (pickMode) {
      // PICK-MODE head (PICK_STEPS): Select matrix → Test → Method, each
      // gated on the previous pick — mirrors the in-form cascade.
      const head: StepState[] = [
        pickMatrix ? 'done' : 'active',
        pickMatrix ? (pickTest ? 'done' : 'active') : 'todo',
        pickTest ? (effTestMethodId ? 'done' : 'active') : 'todo',
      ];
      return [...head, ...tail];
    }
    // Analysis-Queue / existing-batch head (WORKFLOW_STEPS): Select test · New.
    return effTestMethodId
      ? ['done', 'done', ...tail]
      : ['active', 'todo', ...tail];
  }, [pickMode, pickMatrix, pickTest, effTestMethodId, jobOids, prepBatch, instrument, loaded, sorted, rows, instrumentRows, reagentRows, stagedInstruments, stagedReagents]);

  // Post-Sort the built run sequence is 0-BASED (0,1,2,… — legacy parity, see
  // the QC sequence screenshot: Method Blank=0, CCV Low=1, …).
  const renumber = (arr: SeqRow[]) => arr.map((r, i) => ({ ...r, sort: i }));
  // OG QCBatchController Load guard (:6572) gates on Job+Matrix+Test+Method only —
  // the Instrument check is explicitly commented out there, so instrument is
  // optional at Load. prepBatch==='N/A' keeps the prep-import path separate.
  const loadEnabled = jobOids.length > 0 && prepBatch === 'N/A';

  // OK dirty-gate: true once the sequence/header changed vs the loaded baseline,
  // or an other-tab (reagent/instrument) edit was made. Drives the edit-mode OK.
  const editDirty = useMemo(
    () => tabsDirty || (editBaseline.current !== null
      && editSig(rows, roomTemp, humidity, comment) !== editBaseline.current),
    [tabsDirty, rows, roomTemp, humidity, comment]);

  // ── display helpers for the pickers ──
  // For a SAVED batch show the header's stored job LABELS directly — the pending-
  // jobs list (jobsList) doesn't contain an already-processed batch's jobs, so
  // resolving through it would render blank.
  const jobLabel = existingHeader?.jobIds
    ? existingHeader.jobIds
    // Prep-batch import copies the prep batch's Jobid onto the header (OG
    // QCBatchController.cs :1858 `curqC.Jobid = objSamplePrep.Jobid`). A prep
    // import uses NO Job picker (jobOids stays empty), so derive the Job label
    // from the imported samples' DISTINCT JobIDs — same source onOk saves the
    // header Jobid from (line ~815). Without this the Job field showed blank.
    : prepBatch !== 'N/A'
      ? ([...new Set(loadedSamples.map(s => s.jobID).filter((v): v is string => !!v))].join(';') || 'Select job…')
      : jobOids.length
        ? jobsList.filter(j => jobOids.includes(j.jobOid)).map(j => j.jobID).join(';')
        : 'Select job…';
  // Closed-field shows the InstrumentName VALUE only (legacy XAF
  // gridLookup.TextFormatString = "{2}" → DataTable position 2 = AssignedName).
  const instrLabel = instrument
    ? (instrList.find(x => x.instrumentOid === instrument)?.instrumentName ?? instrument)
    : 'Select instrument…';

  // ── actions ──
  function toggleJob(oid: string) {
    setJobOids(prev => (prev.includes(oid) ? prev.filter(x => x !== oid) : [...prev, oid]));
    if (loaded) { setLoaded(false); setSorted(false); setRows([]); setLoadedSamples([]); }
  }
  function pickInstr(oid: string) {
    setInstrument(prev => (prev === oid ? null : oid));
    if (instrument !== oid) setOpenPop(null);
  }
  // Import a Sample Prep Batch's prepared samples (legacy QCBatchController
  // ObjectSpace_ObjectChanged "PrepBatchID" branch :1847-2156): selecting a prep
  // batch seeds the grid with its Sample-type members (in the SAME shape as Load),
  // copies the batch's Room Temp / Humidity onto the header (:1857-1861), and leaves
  // the user to Sort (QC interleave) + OK — where the SampleParameter linkage happens
  // through the normal Create path. Mutually exclusive with Job + Load.
  async function pickPrep(id: string) {
    setPrepBatch(id);
    setOpenPop(null);
    if (id === 'N/A') {
      // Switching back to N/A abandons any prep-imported rows (return to a blank load).
      if (loaded) { setLoaded(false); setSorted(false); setRows([]); setLoadedSamples([]); }
      return;
    }
    const batch = prepList.find(b => (b.prepBatchID ?? '') === id);
    if (!batch) return;
    try {
      const samples = await qcBatchApi.loadFromPrepBatch(effTestMethodId, batch.prepBatchOid);
      if (samples.length === 0) {
        notify.warning('No importable samples in this prep batch (its samples may already be in a QC batch).');
        return;
      }
      // Header copy (legacy parity) — the prep batch's prep conditions carry over.
      if (batch.roomTemp) setRoomTemp(batch.roomTemp);
      if (batch.humidity) setHumidity(batch.humidity);
      // Instrument carries over too (user 2026-07-17). The prep Instrument is an
      // Oid string; the QC header shows the name via instrList (same instrument
      // category when the test matches). Take the first if ';'-joined.
      const prepInstr = batch.instrumentOid?.split(';')[0]?.trim();
      if (prepInstr) setInstrument(prepInstr);
      setLoadedSamples(samples);
      // OG parity (QCBatchController :1847-2007): importing a prep batch copies its
      // ALREADY-BUILT sequence (samples + QC interleave) VERBATIM, so the grid opens
      // SORTED — not the pre-Sort sample list. loadedSamples keeps the plain samples
      // so "Previous" can step back to sample-selection.
      const seq = await qcBatchApi.seqFromPrepBatch(batch.prepBatchOid);
      setRows(renumber(seq.map(seqRowToLocal)));
      setLoaded(true); setSorted(true);
      // Milestone success → centered Tier-1 dialog (user request 2026-07-11), not
      // the transient top banner, so the import outcome is acknowledged.
      void showDialog({
        type: 'success',
        title: 'Prep Batch Imported',
        message: `Imported ${id} — ${seq.length} sequence row${seq.length === 1 ? '' : 's'} (samples + QC).`,
        note: 'The QC sequence is loaded. Review and click OK to save.',
      });
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to import the prep batch.'));
    }
  }

  // `force` bypasses the Load-button gate (which also requires an Instrument) so
  // the Analysis Queue "View details" → OK auto-load can populate the grid before
  // the analyst picks an instrument. The load API itself doesn't need the
  // instrument (UI gate only); it's still required before Sort/OK/Save.
  async function loadSamples(opts?: { force?: boolean }) {
    if (!opts?.force && !loadEnabled) return;
    try {
      let samples = await qcBatchApi.load(effTestMethodId, jobOids);
      // Sample Details popup parity (legacy QCBatchController.cs ~line 6650:
      // `objdistsl = qcbatchinfo.lstSamples.Where(a => a.JobID.JobID == id)`)
      // — narrow the job's full pending-sample list down to exactly what the
      // user checked in the popup, every time Load runs this session.
      if (preSelectedSlOids && preSelectedSlOids.length > 0) {
        const keep = new Set(preSelectedSlOids);
        samples = samples.filter(s => keep.has(s.sampleLogInOid));
      }
      setLoadedSamples(samples);
      // Pre-Sort: leave Sort blank (auto-filled on tick).  Seed each row's
      // DilutionCount from the current No. Runs so the correlation holds even
      // when No. Runs was set before Load.
      const runs = Math.max(1, Number(noRuns) || 1);
      setRows(samples.map(s => ({ ...sampleToLocal(s), dilutionCount: runs })));
      setLoaded(true); setSorted(false);
      // Empty-result warning stays (actionable); the success toast was redundant
      // — the rows landing in the grid is itself the confirmation.
      if (samples.length === 0) notify.warning('No pending samples found for the selected job(s).');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to load samples.'));
    }
  }

  function resetBatch() {
    setJobOids([]); setInstrument(null); setTplId(seed.templateID); setPrepBatch('N/A');
    setLoaded(false); setSorted(false); setRows([]); setLoadedSamples([]);
    setTab('seq'); setInstrumentRows([]);
    setPreSelectedSlOids(null); // abandon the Sample Details seed — a full Reset starts truly blank
    if (pickMode) {
      // OG Reset clears Matrix/Test/Method too (QCBatchController :6395-6401) —
      // back to a fully blank pick cascade (the Matrix options stay cached).
      setPickMatrix(null); setPickTest(null); setTestList([]); setMethodList([]);
      setEffMatrix(null); setEffTest(null); setEffMethod(null); setEffTestMethodId('');
      // …and blank the DOWNSTREAM grid-lookups that belonged to the now-cleared
      // Test/Method: their OPTION lists (Job/Instrument/Prep Batch/Template) and
      // the Template value (the seed default doesn't apply in the pick flow).
      // Without this, Reset cleared the cascade but the 4 lookups kept the prior
      // test's rows / template. (user report 2026-07-11)
      setJobsList([]); setInstrList([]); setTplList([]); setPrepList([]);
      setTplId(null);
    }
  }

  // Sort → server-side interleave from the (checked, or all) loaded samples.
  async function buildSequence() {
    const sampleRows = rows.filter(r => r.kind === 'sample');
    const checkedCodes = new Set(sampleRows.filter(r => r.checked).map(r => r.code));
    // Require an explicit selection — do NOT silently sort all samples.
    if (checkedCodes.size === 0) { notify.warning('Select at least one sample (tick its checkbox) before Sort.'); return; }
    // Every SELECTED sample must carry a Sort value — legacy QCsort_Execute
    // (:6953-6959): `SelectedObjects.Where(a => a.Sort <= 0)` blocks the whole Sort.
    // Reachable here because the pre-Sort Sort cell is a number input the analyst can
    // clear back to empty (→ null) after ticking. (fix 2026-08-07)
    const checkedRows = sampleRows.filter(r => r.checked);
    if (checkedRows.some(r => (r.sort ?? 0) <= 0)) {
      notify.warning('Enter the sort value for selected samples');
      return;
    }
    // Build the payload in the analyst's SORT ORDER — legacy AddSequence takes the
    // sample list as `…Where(x => x.SampleID != null).OrderBy(a => a.Sort)`
    // (QCBatchController.cs:8343), i.e. the run order the analyst set by tick order
    // (each tick assigns max+1) or by typing into the Sort cell. Previously this
    // filtered `loadedSamples`, which is the SP's load order (ORDER BY JobID,
    // SampleNo) — so a deliberate out-of-order selection was silently discarded and
    // the built sequence differed from OG. (fix 2026-08-07)
    //
    // Values come from the CURRENT grid row, not the stale loaded one, so a Sort
    // preserves the analyst's pre-Sort edits: DilutionCount (drives R-expansion
    // server-side — legacy :6980), plus SampleAmount + FinalVol which have no source
    // column and would otherwise blank out after Sort.
    const loadedByCode = new Map(loadedSamples.map(s => [s.sysSampleCode ?? '', s]));
    const toSort = checkedRows
      .slice()
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
      .flatMap(r => {
        const s = loadedByCode.get(r.code);
        return s
          ? [{ ...s, dilutionCount: r.dilutionCount, finalVolMl: r.finalVol || null, sampleAmount: r.sampleAmount || null }]
          : [];
      });
    if (toSort.length === 0) { notify.warning('Select at least one sample (tick its checkbox) before Sort.'); return; }
    // Instrument required at Sort (legacy QCBatchController.cs :7154-7173, twin of
    // the prep Sort check): none selected + test HAS linked instruments → pick one;
    // none selected + test has NONE → the "not linked to this test" message (verbatim
    // OG). Instrument is NOT required at Load. Skipped on prep-batch import (the
    // instrument carries over from the imported batch, and that path sets sorted).
    if (prepBatch === 'N/A' && !instrument) {
      if (instrList.length > 0) notify.warning('Please select an instrument.');
      else notify.error('Instrument has not been linked to this test. Get assistance from your LIMS administrator to add the instruments.');
      return;
    }
    // Prep-batch gate (legacy QCBatchController.cs:13517) — a selected sample whose
    // test method requires prep must have that prep complete before Sort.  Server
    // enforces this too; this mirror gives the message the instant Sort is clicked.
    if (toSort.some(s => s.requiresPrep)) {
      notify.warning('Sample preparation process is required.');
      return;
    }
    try {
      const seq = await qcBatchApi.buildSequence(
        effTestMethodId, tplId, Number(noRuns) || 1, toSort);
      setRows(renumber(seq.map(seqRowToLocal)));
      setSorted(true);
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to build the QC sequence.'));
    }
  }

  // Previous ← step back from the built sequence to sample selection: drops the
  // interleaved QC rows, restores the loaded samples, unlocks the detail fields,
  // and brings the Sort button back so the analyst can re-select and re-Sort.
  // Legacy QCprevious_Execute parity: re-PULL the base samples from the server
  // (not a client-side snapshot restore) — strips the built QC/dilution rows and
  // re-queries the pending samples for the selected job(s), re-seeding Sample
  // rows ×No.Runs. loadSamples() already applies the Sample-Details pre-selection
  // filter and reseeds dilutionCount from No.Runs, so it IS the OG re-pull; it
  // sets sorted=false. So a sample that became pending (or was released) since the
  // last Load is reflected, matching OG rather than a stale in-memory snapshot.
  function goPrevious() {
    // Prep-batch import has NO Job picker (jobOids is empty), so loadSamples()
    // would re-query with no jobs and blank the grid. The prep batch's base
    // samples are fixed (nothing to re-pull), so restore the pre-Sort Sample
    // rows straight from the imported set and drop the interleaved QC rows.
    if (prepBatch !== 'N/A') {
      const runs = Math.max(1, Number(noRuns) || 1);
      setRows(loadedSamples.map(s => ({ ...sampleToLocal(s), dilutionCount: runs })));
      setSorted(false);
      return;
    }
    void loadSamples();
  }

  // Manually add a QC row under the selected parent row (legacy QCadd_Execute
  // parity, ported server-side in QcSequenceBuilder.AddQc — naming/compatibility/
  // renumbering are all computed there, not duplicated here).
  async function addQc(q: QcTypeOption) {
    const selected = rows.filter(r => r.checked);
    // Legacy QCadd_Execute requires EXACTLY one selected grid row: >1 shows
    // "Select only one check box." (Model.xafml LocalizationItem
    // "selectonlyonechkbox", QCBatchController.cs :11524); 0 selected is a
    // silent no-op in legacy (the Action just isn't wired to run) — this port
    // keeps the ＋ button always clickable post-Sort, so it surfaces the same
    // message for 0 as a friendlier equivalent of that no-op.
    if (selected.length > 1) { notify.warning('Select only one check box.'); return; }
    if (selected.length === 0) { notify.warning('Select a row first, then click ＋ to add QC under it.'); return; }
    const parent = selected[0];
    if (!parent) return;
    try {
      const seq = await qcBatchApi.addQc(
        effTestMethodId, parent.sort ?? 0, q.qcTypeOid, rows.map(toWireRow));
      setRows(renumber(seq.map(seqRowToLocal)));
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to add QC.'));
    }
  }

  // Remove a row (legacy QCremove_Execute parity, ported server-side in
  // QcSequenceBuilder.RemoveQc — dependency checks + renumbering computed there).
  async function removeRow(id: string) {
    const target = rows.find(r => r.id === id);
    if (!target) return;
    try {
      const seq = await qcBatchApi.removeQc(effTestMethodId, target.sort ?? 0, rows.map(toWireRow));
      setRows(renumber(seq.map(seqRowToLocal)));
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to remove QC row.'));
    }
  }
  // Sort column auto-fills on check / resets to 0 on uncheck, click order (1-based).
  // Checking row R: R.sort = (max sort across checked rows) + 1, so first = 1.
  // Unchecking row R: R.sort = 0; every other row with sort > R's old sort is
  // decremented by 1 so the remaining checked rows stay contiguous (1,2,3,…).
  function toggleSeqRow(id: string) {
    setRows(prev => {
      const r = prev.find(x => x.id === id);
      if (!r) return prev;
      if (r.checked) {
        const oldSort = r.sort ?? 0;
        return prev.map(x =>
          x.id === id              ? { ...x, checked: false, sort: 0 }
          : (x.sort != null && x.sort > oldSort) ? { ...x, sort: x.sort - 1 }
          : x);
      } else {
        const max = prev.reduce((m, x) => x.checked && x.sort != null && x.sort > m ? x.sort : m, 0);
        return prev.map(x => x.id === id ? { ...x, checked: true, sort: max + 1 } : x);
      }
    });
  }
  // "Select all" / "deselect all" — when ON, assign sorts 1..N in grid order
  // so the analyst gets the obvious top-to-bottom run sequence; when OFF, reset
  // every sort to 0 (the default).
  function toggleAllSeq(on: boolean) {
    setRows(prev => {
      if (!on) return prev.map(r => ({ ...r, checked: false, sort: 0 }));
      let next = 0;
      return prev.map(r => ({ ...r, checked: true, sort: ++next }));
    });
  }
  // Post-Sort checkbox semantics differ from pre-Sort: the Sort column is
  // read-only (drag-driven) once the sequence is built, so checking a row here
  // just marks it for a grid action (Add QC) — legacy grid multi-select parity
  // (QCBatchController.cs QCadd_Execute :11264) — and must NOT touch `.sort`.
  function toggleSeqRowChecked(id: string) {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, checked: !r.checked } : r)));
  }
  function toggleAllChecked(on: boolean) {
    setRows(prev => prev.map(r => ({ ...r, checked: on })));
  }
  // Edit handler for the inline-editable grid cells: Sort, DilutionCount,
  // SampleAmount, FinalVol(ml), ExcludedReport (legacy QCBatchController.cs:5011).
  function updateRow<K extends keyof SeqRow>(id: string, key: K, value: SeqRow[K]) {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, [key]: value } : r)));
  }
  // ExcludedReport is the ONE per-action write in legacy ALPACA (QCBatchController
  // :5595 persists SampleParameter.NotReport the instant the box is toggled, not on
  // Save). Update the grid optimistically, then — for an EXISTING row on a SAVED
  // batch — fire the immediate PATCH so it survives navigating away without a full
  // Save. Staged (pre-save) rows have no seqOid yet and persist with the batch.
  async function onToggleExcludedReport(row: SeqRow, checked: boolean) {
    updateRow(row.id, 'excludedReport', checked);
    if (!savedBatchId || !row.seqOid) return;
    try {
      await qcBatchApi.setExcludedReport(savedBatchId, row.seqOid, checked);
    } catch (e) {
      // Revert the optimistic toggle on failure so the grid matches the DB.
      updateRow(row.id, 'excludedReport', !checked);
      notify.error(apiErrMsg(e, 'Failed to save ExcludedReport.'));
    }
  }
  // No. Runs is correlated to the DilutionCount column: changing it in the header
  // updates every Sample row's DilutionCount (legacy QCBatchController line 2170,
  // filtered to QCType == "Sample").
  function onNoRunsChange(v: string) {
    setNoRuns(v);
    const n = Math.max(1, Number(v) || 1);
    setRows(prev => prev.map(r => (r.kind === 'sample' ? { ...r, dilutionCount: n } : r)));
  }
  // "Add Dilution" (context menu) — server (QcSequenceBuilder.AddDilution, legacy
  // parity) mints "<parentCode>R<n>", inherits the parent's QCType/linkage/metadata,
  // places it with the parent's dilutions, and resequences. The target row is
  // the CHECKED row (grid selection), not the right-clicked row — matching the
  // same "exactly one checked row" contract as Add QC (legacy's AddDilution
  // just takes SelectedObjects.FirstOrDefault() with no validation; this port
  // adds an explicit validation instead of silently picking one).
  async function addDilution() {
    closeCtx();
    const selected = rows.filter(r => r.checked);
    if (selected.length > 1) { notify.warning('Select only one check box.'); return; }
    if (selected.length === 0) { notify.warning('Select a row first, then right-click to Add Dilution.'); return; }
    const parent = selected[0];
    if (!parent || parent.sort == null) return;
    if (parent.kind === 'dilution') { notify.warning('Cannot add a dilution to a dilution row.'); return; }
    try {
      const seq = await qcBatchApi.addDilution(effTestMethodId, parent.sort, rows.map(toWireRow));
      setRows(renumber(seq.map(seqRowToLocal)));
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to add dilution.'));
    }
  }

  // ── drag & drop reorder ──
  const dragId = useRef<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  // Drag-drop reorder — the server (QcSequenceBuilder.MoveRow, legacy DRAGROW
  // parity) validates whether the drop is allowed for that row's relationships and
  // returns the resequenced list; a disallowed drop surfaces as a warning and the
  // grid is left untouched.
  async function onDrop(targetId: string) {
    const from = dragId.current;
    setDropTarget(null);
    dragId.current = null;
    if (!from || from === targetId) return;
    const dragRow = rows.find(r => r.id === from);
    const targetRow = rows.find(r => r.id === targetId);
    if (!dragRow || !targetRow || dragRow.sort == null || targetRow.sort == null) return;
    try {
      const seq = await qcBatchApi.moveRow(
        effTestMethodId, dragRow.sort, targetRow.sort, rows.map(toWireRow));
      setRows(renumber(seq.map(seqRowToLocal)));
    } catch (e) {
      notify.warning(apiErrMsg(e, 'That move is not allowed.'));
    }
  }

  // ── context menu ──
  const [ctx, setCtx] = useState<{ x: number; y: number; row: SeqRow } | null>(null);
  function closeCtx() { setCtx(null); }

  // "Copy To All Cell" (legacy QCBatchController.cs:3286) — the last editable cell
  // the user focused, limited to the legacy allow-list (SampleAmount / FinalVol).
  // The context-menu action copies the right-clicked row's value for that column
  // to every CHECKED (selected) row — now that post-Sort grid selection exists,
  // this matches legacy's real "fill the selected rows" batch-edit semantics
  // rather than blanket-filling every row on the grid.
  const [focusedCol, setFocusedCol] = useState<'sampleAmount' | 'finalVol' | null>(null);
  function copyToAll(row: SeqRow) {
    closeCtx();
    if (!focusedCol) return;
    const selected = rows.filter(r => r.checked);
    if (selected.length === 0) { notify.warning('Select at least one row to copy to.'); return; }
    const value = row[focusedCol];
    const selectedIds = new Set(selected.map(r => r.id));
    setRows(prev => prev.map(r => (selectedIds.has(r.id) ? { ...r, [focusedCol]: value } : r)));
    qcMsg(`Copied ${focusedCol === 'finalVol' ? 'FinalVol' : 'SampleAmount'} to ${selected.length} selected row${selected.length > 1 ? 's' : ''}.`, 'Copied');
  }

  // OK → persist the batch (mint race-safe QCBatchID, link samples) via the
  // shared save flow ("Do you want to Save?" → create → "Saved" → back to queue).
  function onOk() {
    if (!sorted) { notify.warning('Sort the sequence before saving.'); return; }
    // Header Jobid (';'-joined). A prep-batch import uses NO Job picker, so jobOids
    // is empty — derive it from the imported samples' DISTINCT JobIDs instead, so
    // the header carries the jobs (OG copies prep.Jobid, QCBatchController.cs :1858;
    // otherwise a prep-imported batch would save with an EMPTY Jobid and show no
    // jobs in the OG list). The normal Job → Load path keeps using the selected jobs.
    const jobIdsLabel = prepBatch !== 'N/A'
      ? [...new Set(loadedSamples.map(s => s.jobID).filter((v): v is string => !!v))].join(';')
      : jobsList.filter(j => jobOids.includes(j.jobOid)).map(j => j.jobID).filter(Boolean).join(';');
    const instr = instrList.find(i => i.instrumentOid === instrument);
    // Save only fires after Sort (the controller blocks empty sequences), so
    // every row's sort has been numbered by then; toWireRow's `?? 0` is a
    // safety floor.
    const sequence: QcSeqRow[] = rows.map(toWireRow);
    const body: QcCreateBody = {
      testMethodId:   effTestMethodId,
      templateId:     tplId,
      jobIds:         jobIdsLabel || null,
      instrumentOid:  instrument,
      instrumentName: instr?.instrumentName ?? null,
      noRuns:         Number(noRuns) || 1,
      roomTemp:       roomTemp || null,
      humidity:       humidity || null,
      comments:       comment.trim() || null,
      sequence,
      // Reagents/instruments staged before the batch existed — legacy parity,
      // persisted atomically with Create (see the staging state above).
      stagedReagents: stagedReagents.length ? stagedReagents.map(r => ({
        reagentId: r.reagentID ?? '', reagentName: r.reagentName ?? '', prepType: r.prepType,
        expiredDate: r.expiryDate, vendorLT: r.vendorLT, preparedBy: r.preparedBy,
        datePrepared: r.datePrepared, dateReceived: r.dateReceived, vendorOid: null,
      })) : undefined,
      stagedInstrumentOids: stagedInstruments.length ? stagedInstruments.map(i => i.instrumentOid) : undefined,
    };

    // OG QCsort "Ok" branch (QCBatchController.cs :7213-7330): a batch whose test
    // picked an SDMS TEMPLATE (NPTemplateID != null) is NOT saved here — OG stuffs
    // the built sequence (objABinfo.lstQCBatchSequence + BindQCBatchSequenceToDataTable)
    // and navigates to the SDMS result-entry dashboard; the AnalyticalBatch is
    // created (NewABID) from SDMS's Save. So hand `body` to SDMS WITHOUT saving here.
    // `onSdmsSeed` means we were opened as the popup FROM the SDMS screen, so SDMS
    // is the destination by definition — hand back even if no template resolved,
    // rather than dropping into the plain-batch "Do you want to Save?" flow below
    // (which is for the QC Batches / Analysis Queue entry points).
    if (tplId != null || onSdmsSeed) {
      goSdms(body);
      return;
    }

    // Plain batch (no SDMS template) — save now and return to the QC Batches list.
    // Captured inside `save` so the milestone-success MODAL (§13) can show the
    // minted QC Batch ID + detail rows after the record is created (parity with the
    // prep form's created-ID dialog, user 2026-07-17).
    let minted: QcCreateResult | null = null;
    void runSave({
      save: () => qcBatchApi.create(body)
        .then(result => { minted = result; setSavedBatchId(result.abOid); }),
      // §13 milestone-success MODAL (a new QC batch + ID was created) — green,
      // big QC Batch ID + detail rows, matching the prep form's created-ID dialog.
      successDialog: () => ({
        type: 'success',
        title: 'QC Batch Created',
        idLabel: 'QC Batch ID',
        idValue: minted?.qcBatchId ?? '',
        rows: [
          { label: 'Test', value: effTest || '—' },
          { label: 'Job(s)', value: jobIdsLabel || '—' },
          { label: 'Runs', value: String(minted?.seqCount ?? sequence.length) },
          { label: 'Samples linked', value: String(minted?.samplesLinked ?? 0) },
        ],
        note: 'Returning to the QC Batches list.',
      }),
      onSaved: () => onBack(),
      formName: 'QC Batches',
      auditAction: 'create',
      // Surface the server's OWN reason on a save guard (dup-ID race, validation),
      // not the generic "Save failed." — same OG-style messaging as every other action.
      onError: (e) => notify.error(apiErrMsg(e, 'Save failed. Please try again.')),
    });
  }

  // Save (edit mode) → A1 reconcile-on-Save: persist the current grid + Room Temp/
  // Humidity back to the saved batch via the shared save flow ("Do you want to
  // Save?" → PUT → "Saved successfully."), then refresh from the server (fresh
  // seqOids for added rows, removed rows gone) and drop back to view mode.
  function saveEdits() {
    if (!savedBatchId || !existingHeader) return;
    const sequence: QcSeqRow[] = rows.map(toWireRow);
    void runSave({
      save: () => qcBatchApi.updateBatch(savedBatchId, {
        roomTemp: roomTemp || null,
        humidity: humidity || null,
        comments: comment.trim() || null,
        sequence,
      }).then(() => loadDetail(savedBatchId)),
      successMessage: 'Saved successfully.',
      successAs: 'dialog',   // shared success MODAL (user 2026-07-17 — consistent with the create dialog)
      onSaved: () => setViewOnly(true),
      formName: 'QC Batches',
      auditAction: 'edit',
      onError: (e) => notify.error(apiErrMsg(e, 'Save failed. Please try again.')),
    });
  }

  // (Delete lives on the QC Batches LIST view, not the detail — OG parity; the
  // detail dashboard has no Delete action. 2026-07-11.)

  // Rollback → legacy QCBatchRollback parity: mandatory reason prompt (blocks
  // on empty, matches legacy "Rollbackempty"), then revert the saved batch to
  // Pending Result Entry and return to the queue (the released samples no
  // longer belong on this screen).
  async function rollbackBatch() {
    if (!savedBatchId) return;
    const reason = await prompt({
      title: 'QC Batch Rollback',
      message: 'Enter a reason for the rollback.',
      placeholder: 'Rollback reason…',
      required: true,
      confirmLabel: 'Rollback',
    });
    if (reason === null) return;   // cancelled

    try {
      await qcBatchApi.rollback(savedBatchId, reason);
      qcMsg('QC Batch rolled back.', 'Rollback');
      onBack();
    } catch (e) {
      notify.error(apiErrMsg(e, 'Rollback failed. Please try again.'));
    }
  }

  // Add Samples to a saved batch — legacy AddQCBatchSample_* parity. OG STAGES the
  // picked samples into the QC sequence grid and persists NOTHING until the final
  // "OK" (the reconcile-on-Save UpdateBatch inserts + links the new rows — see
  // AI_Analysis_QcBatch_Update.sql "ADDED rows"). So here we only append them to
  // the local grid as new Sample rows (no seqOid → treated as ADDED on Save); the
  // old code called the API immediately, which persisted early, returned a
  // PARAMETER-link count (2 for a 2-parameter sample, not 1 sample), and never
  // refreshed the grid. (user report 2026-07-11)
  function addSamples(selected: QcSample[]) {
    if (selected.length === 0) return;
    // Skip any sample already present in the grid (candidates are pre-filtered, but
    // guard against a double-add). Count = SAMPLES added (not parameter links).
    const present = new Set(rows.map(r => r.code));
    const fresh = selected.filter(s => !present.has(s.sysSampleCode ?? ''));
    if (fresh.length === 0) { qcMsg('The selected sample(s) are already in the batch.', 'Add Samples', 'info'); return; }
    const runs = Math.max(1, Number(noRuns) || 1);
    const newRows = fresh.map(s => ({ ...sampleToLocal(s), dilutionCount: runs }));
    // Newly added samples go at the BOTTOM of the grid (user request 2026-07-11).
    setRows(prev => renumber([...prev, ...newRows]));
    qcMsg(`${fresh.length} sample${fresh.length === 1 ? '' : 's'} added. Click OK to save.`, 'Samples Added');
  }

  // Reagents (Others tab) — legacy ReagentLink_Execute parity. Loads once a
  // batch is saved; refetched after every link/unlink.
  async function loadReagents(abOid: string) {
    try {
      setReagentRows(await qcBatchApi.linkedReagents(abOid));
    } catch {
      notify.error('Could not load linked reagents.');
    }
  }
  useEffect(() => { if (savedBatchId) void loadReagents(savedBatchId); }, [savedBatchId]);

  // Reagent picker OK — reconcile the picker's final staged set against the
  // current state (mockup commitReagents parity, which wholesale REPLACES
  // reagentRows).  For a saved batch, computes delta and calls link/unlink
  // for each item; for a pre-save batch, replaces the local stagedReagents
  // (dedup by reagentID; catalog rows carried through so Create ships them).
  async function reconcileReagents(final: StagedReagent[]) {
    if (!savedBatchId) {
      // Un-saved: replace stagedReagents with the picker's set. Newly picked
      // rows carry catalog; pre-existing kept rows do NOT (they were already
      // in stagedReagents so we preserve them by reagentID lookup).
      const prevByKey = new Map(stagedReagents.map(r => [r.reagentID ?? '', r] as const));
      const next: QcAvailableReagent[] = final.map(s =>
        s.catalog ?? prevByKey.get(s.reagentID) ?? {
          // fallback: minimal shape (shouldn't normally hit — picker seeds from stagedReagents)
          reagentID: s.reagentID, reagentName: s.reagentName, prepType: s.prepType, expiryDate: s.expiryDate,
          test: null, datePrepared: null, preparedBy: null, division: null, department: null,
          createdDate: null, dateReceived: null, vendor: null, vendorLT: null,
        });
      setStagedReagents(next);
      const added = final.filter(s => !prevByKey.has(s.reagentID)).length;
      const removed = stagedReagents.filter(r => !final.some(s => s.reagentID === r.reagentID)).length;
      if (added || removed) qcMsg(`${next.length} reagent(s) on the batch.`, 'Reagents');
      return;
    }
    // Saved batch: apply delta via the existing link/unlink APIs. link clones
    // the catalog row into a fresh dbo.Reagent; unlink drops the junction row
    // (the master Reagent is left in place, per legacy).
    const keptOids = new Set(final.map(s => s.linkedOid).filter((v): v is string => !!v));
    const toRemove = reagentRows.filter(r => !keptOids.has(r.oid));
    const toAdd    = final.filter(s => !s.linkedOid && s.catalog).map(s => s.catalog!);
    if (toRemove.length === 0 && toAdd.length === 0) return;
    try {
      for (const r of toRemove) await qcBatchApi.unlinkReagent(savedBatchId, r.oid, r.reagentID);
      for (const c of toAdd)    await qcBatchApi.linkReagent  (savedBatchId, c);
      await loadReagents(savedBatchId);
      const parts = [
        toAdd.length    ? `${toAdd.length} added`    : '',
        toRemove.length ? `${toRemove.length} removed` : '',
      ].filter(Boolean).join(', ');
      setTabsDirty(true);
      qcMsg(`Reagents updated — ${parts}.`, 'Reagents');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Updating reagents failed.'));
    }
  }

  async function unlinkSelectedReagents() {
    if (!savedBatchId) {
      if (stagedReagentSel.size === 0) { notify.warning('Select a reagent first.'); return; }
      setStagedReagents(prev => prev.filter(r => !stagedReagentSel.has(r.reagentID ?? '')));
      setStagedReagentSel(new Set());
      return;
    }
    if (reagentSel.size === 0) { notify.warning('Select a reagent first.'); return; }
    try {
      for (const oid of reagentSel) {
        const row = reagentRows.find(r => r.oid === oid);
        await qcBatchApi.unlinkReagent(savedBatchId, oid, row?.reagentID ?? null);
      }
      setReagentSel(new Set());
      await loadReagents(savedBatchId);
      setTabsDirty(true);
      qcMsg('Reagent(s) unlinked.', 'Reagents');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Unlinking reagent failed.'));
    }
  }
  const toggleReagentSel = (oid: string) =>
    setReagentSel(prev => { const n = new Set(prev); if (n.has(oid)) n.delete(oid); else n.add(oid); return n; });
  const toggleStagedReagentSel = (reagentId: string) =>
    setStagedReagentSel(prev => { const n = new Set(prev); if (n.has(reagentId)) n.delete(reagentId); else n.add(reagentId); return n; });
  // Others-tab header "select all" checkbox — mirrors the sequence grid's
  // toggleAllSeq/toggleAllChecked pattern (§18.6 identical-form-structure).
  const toggleAllReagentSel = (on: boolean) =>
    savedBatchId
      ? setReagentSel(on ? new Set(reagentRows.map(r => r.oid)) : new Set())
      : setStagedReagentSel(on ? new Set(stagedReagents.map(r => r.reagentID ?? '')) : new Set());
  const allReagentChecked = savedBatchId
    ? reagentRows.length > 0 && reagentRows.every(r => reagentSel.has(r.oid))
    : stagedReagents.length > 0 && stagedReagents.every(r => stagedReagentSel.has(r.reagentID ?? ''));
  // COA (Certificate of Analysis) — legacy PreviewCOA_Execute for "LT"-prefixed
  // (NOT "LLT") reagent lots only ("LLT" dynamic-report generation is out of
  // scope). The column only renders a link when the row's server-computed
  // `hasCoa` is true (the reagent has an uploaded cert), so there are no dead
  // links (legacy hides the button the same way). OPENS the PDF in a NEW TAB —
  // legacy ShowPdfInNewTab parity (was a forced download before).
  const openCoa = async (reagentId: string | null) => {
    if (!reagentId) return;
    try {
      await qcBatchApi.openReagentCoa(reagentId);
    } catch {
      notify.warning('No certificate on file for this reagent.');
    }
  };

  // Instruments (Others tab) — legacy InstrumentLink_Execute parity. Loads once
  // a batch is saved; refetched after every link/unlink.
  async function loadLinkedInstruments(abOid: string) {
    try {
      setInstrumentRows(await qcBatchApi.linkedInstruments(abOid));
    } catch {
      notify.error('Could not load linked instruments.');
    }
  }
  useEffect(() => { if (savedBatchId) void loadLinkedInstruments(savedBatchId); }, [savedBatchId]);

  // Instrument picker OK — reconcile the picker's final staged set (mockup
  // commitInstruments parity, which wholesale REPLACES instrumentRows).
  // Saved batch: delta link/unlink; pre-save: replace stagedInstruments.
  async function reconcileInstruments(final: QcInstrument[]) {
    if (!savedBatchId) {
      const dedupByOid = new Map<string, QcInstrument>();
      for (const i of final) dedupByOid.set(i.instrumentOid, i);
      const next = Array.from(dedupByOid.values());
      const added = final.filter(i => !stagedInstruments.some(s => s.instrumentOid === i.instrumentOid)).length;
      const removed = stagedInstruments.filter(i => !final.some(s => s.instrumentOid === i.instrumentOid)).length;
      setStagedInstruments(next);
      if (added || removed) qcMsg(`${next.length} instrument(s) on the batch.`, 'Instruments');
      return;
    }
    const preOids = new Set(instrumentRows.map(i => i.instrumentOid));
    const finalOids = new Set(final.map(i => i.instrumentOid));
    const toRemove = instrumentRows.filter(i => !finalOids.has(i.instrumentOid));
    const toAdd    = final.filter(i => !preOids.has(i.instrumentOid));
    if (toRemove.length === 0 && toAdd.length === 0) return;
    try {
      for (const i of toRemove) await qcBatchApi.unlinkInstrument(savedBatchId, i.instrumentOid, i.instrumentName);
      for (const i of toAdd)    await qcBatchApi.linkInstrument  (savedBatchId, i.instrumentOid, i.instrumentName);
      await loadLinkedInstruments(savedBatchId);
      const parts = [
        toAdd.length    ? `${toAdd.length} added`    : '',
        toRemove.length ? `${toRemove.length} removed` : '',
      ].filter(Boolean).join(', ');
      setTabsDirty(true);
      qcMsg(`Instruments updated — ${parts}.`, 'Instruments');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Updating instruments failed.'));
    }
  }

  async function unlinkSelectedInstruments() {
    if (!savedBatchId) {
      if (stagedInstrumentSel.size === 0) { notify.warning('Select an instrument first.'); return; }
      setStagedInstruments(prev => prev.filter(i => !stagedInstrumentSel.has(i.instrumentOid)));
      setStagedInstrumentSel(new Set());
      return;
    }
    if (instrumentSel.size === 0) { notify.warning('Select an instrument first.'); return; }
    try {
      for (const oid of instrumentSel) {
        const row = instrumentRows.find(r => r.instrumentOid === oid);
        await qcBatchApi.unlinkInstrument(savedBatchId, oid, row?.instrumentName ?? null);
      }
      setInstrumentSel(new Set());
      await loadLinkedInstruments(savedBatchId);
      setTabsDirty(true);
      qcMsg('Instrument(s) unlinked.', 'Instruments');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Unlinking instrument failed.'));
    }
  }
  const toggleInstrumentSel = (oid: string) =>
    setInstrumentSel(prev => { const n = new Set(prev); if (n.has(oid)) n.delete(oid); else n.add(oid); return n; });
  const toggleStagedInstrumentSel = (oid: string) =>
    setStagedInstrumentSel(prev => { const n = new Set(prev); if (n.has(oid)) n.delete(oid); else n.add(oid); return n; });
  const toggleAllInstrumentSel = (on: boolean) =>
    savedBatchId
      ? setInstrumentSel(on ? new Set(instrumentRows.map(i => i.instrumentOid)) : new Set())
      : setStagedInstrumentSel(on ? new Set(stagedInstruments.map(i => i.instrumentOid)) : new Set());
  const allInstrumentChecked = savedBatchId
    ? instrumentRows.length > 0 && instrumentRows.every(i => instrumentSel.has(i.instrumentOid))
    : stagedInstruments.length > 0 && stagedInstruments.every(i => stagedInstrumentSel.has(i.instrumentOid));

  // Copy From Previous Batch — legacy SamplePreparationController copy-from-previous
  // (QC branch, :6287-6353): APPEND + de-dup the chosen prior batch's reagents /
  // instruments, and — for "Others" — copy its header Comment. Options mirror the
  // OG CopyFromSamplePrepBatch checkboxes: Reagent · Instrument · Others · All the
  // Above (All = all three). Reuses the same add paths the pickers use (link on a
  // saved batch, stage otherwise). Sequence / Room Temp / Humidity are NOT copied
  // (legacy doesn't either).
  async function copyFromPrevious(sourceAbOid: string, copyReagents: boolean, copyInstruments: boolean, copyComment: boolean) {
    try {
      let addedR = 0, addedI = 0; let comm = false;
      if (copyComment) {
        // OG "Others" — curBatch.Comments = source.Comments (SamplePreparation
        // Controller.cs :6316): REPLACE this batch's Comment with the source's.
        const detail = await qcBatchApi.getDetail(sourceAbOid);
        setComment(detail.header.comments ?? '');
        comm = true;
      }
      if (copyReagents) {
        const src = await qcBatchApi.linkedReagents(sourceAbOid);
        const toAvail = (r: QcLinkedReagent): QcAvailableReagent => ({
          reagentID: r.reagentID, reagentName: r.reagentName, prepType: r.prepType, expiryDate: r.expiredDate,
          test: null, datePrepared: r.datePrepared, preparedBy: r.preparedBy, division: null, department: null,
          createdDate: null, dateReceived: r.dateReceived, vendor: r.vendorName, vendorLT: r.vendorLT,
        });
        if (savedBatchId) {
          const have = new Set(reagentRows.map(r => r.reagentID));
          for (const r of src) {
            if (!r.reagentID || have.has(r.reagentID)) continue;
            await qcBatchApi.linkReagent(savedBatchId, toAvail(r)); addedR++;
          }
          if (addedR) await loadReagents(savedBatchId);
        } else {
          const have = new Set(stagedReagents.map(r => r.reagentID));
          const add = src.filter(r => r.reagentID && !have.has(r.reagentID)).map(toAvail);
          addedR = add.length;
          if (add.length) setStagedReagents(prev => [...prev, ...add]);
        }
      }
      if (copyInstruments) {
        const src = await qcBatchApi.linkedInstruments(sourceAbOid);
        if (savedBatchId) {
          const have = new Set(instrumentRows.map(i => i.instrumentOid));
          for (const i of src) {
            if (have.has(i.instrumentOid)) continue;
            await qcBatchApi.linkInstrument(savedBatchId, i.instrumentOid, i.instrumentName); addedI++;
          }
          if (addedI) await loadLinkedInstruments(savedBatchId);
        } else {
          const have = new Set(stagedInstruments.map(i => i.instrumentOid));
          const add = src.filter(i => !have.has(i.instrumentOid));
          addedI = add.length;
          if (add.length) setStagedInstruments(prev => [...prev, ...add]);
        }
      }
      // Reagent/instrument copies persist immediately on a saved batch — flag the
      // OK gate (the comment copy is picked up automatically via the signature).
      if (savedBatchId && (addedR || addedI)) setTabsDirty(true);
      const parts: string[] = [];
      if (copyReagents)    parts.push(`${addedR} reagent(s)`);
      if (copyInstruments) parts.push(`${addedI} instrument(s)`);
      if (comm)            parts.push('the comment');
      qcMsg(`Copied ${parts.join(', ')} from the previous batch.`, 'Copy From Previous');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Copy from previous batch failed.'));
    }
  }

  const anySample = rows.some(r => r.kind === 'sample');
  const allChecked = rows.length > 0 && rows.every(r => r.checked);
  const rowClass = (r: SeqRow) => (r.kind === 'sample' ? 'sample' : r.sourceId ? 'child' : 'qc');

  // ── toolbar2 enable/disable (legacy QCBatchController new-batch flow parity,
  //    ctrl 320-374).  `savedBatchId` is null during creation; it will be set
  //    when opening an existing batch (Phase 3), which flips the saved-only
  //    actions on.  Rules:
  //      Project Notes  → enabled once a job is selected (project context)
  //      Add Samples    → AddQCBatchSample.Active = false until the batch is saved
  //      Rollback       → disabled before batch creation
  //      Copy Prev      → QCprevious.Enabled = false until samples are loaded
  //      Audit Trail    → nothing to audit until the batch is saved
  const canNotes      = jobOids.length > 0 || savedBatchId !== null;
  // Add Samples / Copy From Previous WRITE to the batch — blocked while viewing
  // an existing batch read-only (legacy isview parity).
  const canAddSamples = savedBatchId !== null && !viewOnly;
  const canCopy       = (loaded || savedBatchId !== null) && !viewOnly;
  // Rollback — OG QCBatchRollback.TargetObjectsCriteria "Not (NonStatus In (0,1))"
  // (QCBatchController.cs :312): a saved batch at status >= 2 (Pending Validation
  // onward). It does NOT require edit mode — OG allows rollback in view. The old
  // `savedBatchId && !viewOnly` gate was WRONG: a validated (status >= 2) batch is
  // view-only (batchIsEditable is 0/1 only), so Rollback was UNREACHABLE for the
  // exact batches that need it, while being wrongly enabled for editable 0/1
  // batches that have nothing to roll back. Server SP re-checks the real guards.
  const canRollback   = existingHeader != null && (existingHeader.status ?? -1) >= 2;
  // A batch is EDITABLE at OG EditAction criteria "NonStatus In (0,1)" (:310):
  // status 0 (Pending Result Entry) OR 1 (Pending Submission). Gates Room Temp /
  // Humidity + the Save button + whether Add Samples shows. (The Edit and Delete
  // BUTTONS themselves live on the QC Batches LIST view, not on the detail — OG
  // parity.) Server SP enforces the real guards.
  const batchIsEditable = existingHeader != null && (existingHeader.status === 0 || existingHeader.status === 1);
  // Open SDMS — OG OpenSDMSQcbatch. Active (visible) only for a SAVED/existing
  // batch (:340/:353 — hidden in the fresh create flow, :141/:348); enabled once
  // saved (:7392). OG also gates enable on the "Spreadsheet" nav permission
  // (:161/:183) — AI defers per-nav gating, so it's enabled for any saved batch.
  // Open SDMS — swap to the SDMS Result Entry screen (prototype SCREEN 3),
  // seeded with the batch's test/method/matrix/instrument + built sequence runs.
  //
  // ONE definition of that seed, used by both exits: the normal full-page swap
  // below, and the `onSdmsSeed` callback when this form was opened as a popup FROM
  // SDMS. `pending` is passed in rather than read from state because the create
  // path calls this in the same tick it sets `pendingSdms` (setState is async).
  const buildSdmsSeed = (pending: QcCreateBody | null): SdmsEntrySeed => ({
    test:       effTest ?? seed.test ?? '',
    method:     effMethod ?? seed.method,
    matrix:     effMatrix ?? seed.matrix,
    instrument: instrLabel === 'Select instrument…' ? null : instrLabel,
    analyst:    existingHeader?.createdByName ?? 'Service',
    qcBatchId:  existingHeader?.qcBatchId ?? null, // display QB… (minted in SDMS on Save)
    qcBatchOid: savedBatchId ?? seed.existingAbOid ?? null, // Oid (audit source + sheet load key)
    runs:       rows.map<SdmsRun>(r => ({ code: r.code, qc: r.qc, kind: r.kind === 'sample' ? 'sample' : 'qc' })),
    pendingCreate: pending,
  });
  // Hand back to the SDMS screen that opened us (popup flow), else swap in-page.
  const goSdms = (pending: QcCreateBody | null) => {
    if (onSdmsSeed) { onSdmsSeed(buildSdmsSeed(pending)); return; }
    setPendingSdms(pending);
    setSdmsOpen(true);
  };
  const openSdms = () => goSdms(null);
  // Room Temp / Humidity are editable in the SAME two cases: the normal
  // create flow pre-Sort (unchanged), OR an existing "Pending Submission"
  // batch currently in edit mode — every other General Information field
  // (Job ID/Instrument/Template/Prep/No.Runs) stays locked in both cases.
  const roomHumidityEditable = !sorted || (existingHeader !== null && !viewOnly && batchIsEditable);
  // Audit Trail is now the shared <AppToolbar auditFormName> button (config-gated),
  // so no per-form canAudit gate is needed.

  // SDMS Result Entry — full-page swap out of the QC detail (Back returns here).
  // (`pendingCreate` inside the seed is the OK→SDMS handoff payload — the built
  // sequence + header, saved FROM the SDMS screen because OG defers the
  // AnalyticalBatch save to SDMS. Null when SDMS is opened on an ALREADY-saved
  // batch via the toolbar "Open SDMS".)
  if (sdmsOpen) {
    return (
      <SdmsResultEntryPage
        seed={buildSdmsSeed(pendingSdms)}
        // Opened via the list SDMS button → Back returns straight to the list;
        // opened via this detail's toolbar → Back returns to this detail form.
        onBack={seed.autoOpenSdms ? onBack : () => { setSdmsOpen(false); setPendingSdms(null); }}
      />
    );
  }

  // List "SDMS" flow (seed.autoOpenSdms): never flash the full QC detail form —
  // show a minimal loader until the auto-open effect swaps in the sheet. Keep a
  // Back so a load failure isn't a dead end.
  if (seed.autoOpenSdms && !sdmsOpen) {
    return (
      <div className="qcb-root" style={{ padding: 24 }}>
        <button className="ca-btn" onClick={onBack}>← Back</button>
        <div style={{ marginTop: 16, color: 'var(--ai-muted)' }}>Loading QC batch…</div>
      </div>
    );
  }

  const rootCls = ['qcb-root', giCollapsed ? 'geninfo-collapsed' : '', qcCollapsed ? 'qc-collapsed' : '', fullGrid ? 'fullgrid' : '']
    .filter(Boolean).join(' ');

  return (
    <>
      {/* Shared workflow stepper (WorkflowStepperBar.tsx) — docked in the
          AppShell topbar via portal, same pill Analysis Queue renders, now
          driven by this screen's live stepStatus (steps 1-8; Analysis Queue
          only ever shows step 0 "Select test" active). */}
      {/* Pick-mode swaps the "Select test · New" head for the in-form cascade
          (Select matrix · Test · Method); Analysis-Queue entry keeps the
          default steps. stepStatus is computed to match whichever list. */}
      {/* Not when `embedded`: this portals into the AppShell topbar, so inside the
          SDMS popup it would take over the whole app header (see Props.embedded). */}
      {!embedded && <WorkflowStepperBar stepStatus={stepStatus} steps={pickMode ? PICK_STEPS : BASE_STEPS} />}
      <div className={rootCls} onClick={() => { setOpenPop(null); closeCtx(); }}>
      {/* breadcrumb + head — the crumb names the page we drilled down FROM, which is
          only true for the Analysis Queue entry point, not the SDMS popup. */}
      {!embedded && (
        <div className="crumb">
          <a onClick={onBack}>Analysis Queue</a> / <b>QC Batch</b>
        </div>
      )}
      <div className="pagehead">
        {/* Shared FormTitle (§18.6) — icon/title/load-time chip.  This is a drill-down
            from Analysis Queue (no own nav entry), so icon + title are supplied
            explicitly. */}
        {/* Reset · Load moved to the toolbar `right` slot (below) so they sit WITH
            the form instead of floating in the far top-right corner.  Back
            navigation is the "Analysis Queue" breadcrumb above. */}
        {/* Audit Trail lives on FormTitle in the current project (§18.6) — it
            auto-renders the config-gated AuditTrailButton chip. (Merged 2026-07-07.) */}
        <FormTitle icon="ti-test-pipe" title="QC Batch"
          auditFormName="QC Batches" auditSourceId={savedBatchId ?? undefined} />
      </div>

      {/* Page-level guidance strip — suppressed when embedded in the SDMS popup
          (the dialog is already titled and scoped; the "Step 3." wording belongs to
          the Analysis-Queue walk). Hidden for ALL its variants rather than just the
          Step-3 one, so the strip doesn't pop into existence after Sort. */}
      {!embedded && (
        <div className={`hint ${sorted ? 'ok' : ''}`}>
          {existingHeader
            ? (viewOnly
                ? <span><b>Viewing QC Batch {existingHeader.qcBatchId}</b> — read-only. Click <b>Edit</b> to make changes.</span>
                : <span><b>Editing QC Batch {existingHeader.qcBatchId}.</b> Drag to reorder, QC <b>＋</b> / row <b>−</b> to add/remove, check a row then right-click for <b>Add Dilution</b>.</span>)
            : sorted
            ? <span><b>Run sequence built — {rows.length} runs.</b> Drag to reorder, QC <b>＋</b> / row <b>−</b> to add/remove, check a row then right-click for <b>Add Dilution</b>. Click <b>OK</b> to continue.</span>
            : <span><b>Step 3.</b> Pick <b>Job ID</b> + <b>Instrument</b> then <b>Load</b>, then <b>Sort</b> to build the QC run sequence.</span>}
        </div>
      )}

      {/* Shared <AppToolbar> (§18.6) — form-specific buttons in the `left` slot in
          the legacy order. Audit Trail moved to <FormTitle> above (current-project
          convention — AppToolbar no longer takes auditFormName). Enable/disable
          mirrors the legacy new-batch flow (see canX above). (Merged 2026-07-07.) */}
      <div onClick={e => e.stopPropagation()}>
        <AppToolbar
          // NO Edit and NO Delete on the detail view — OG's EditAction is a
          // ListViewController action (QCBatchController.cs :229/:307) and the OG
          // QC Batch detail dashboard has no Delete either (both are LIST-view
          // actions). So neither onEdit nor onDelete is passed here; Edit and
          // Delete live on the QC Batches LIST view. (2026-07-11, user report.)
          left={
            /* Toolbar order per the OG XAF app (user 2026-07-11): SDMS · Project
               Notes · Add Samples · Rollback · Copy From Previous Batch. */
            <>
              {/* SDMS FIRST — OG OpenSDMSQcbatch. Rendered ONLY for a saved/existing
                  batch (OG Active rule :340/:353), hidden during the fresh create
                  flow. Destination is the separate SDMS track. */}
              {existingHeader && (
                <button className="ca-btn" title="Open the SDMS spreadsheet for this batch"
                  onClick={openSdms}><i className="ti ti-table" /> SDMS</button>
              )}
              <button className="ca-btn" disabled={!canNotes} title={canNotes ? '' : 'Select a job first'}
                onClick={() => setModal('notes')}><i className="ti ti-notes" /> Project Notes{notesCount != null ? ` (${notesCount})` : ''}</button>
              {/* Add Samples — shown ONLY when the batch was opened from the QC
                  Batches list's EDIT button, i.e. an existing batch in edit mode
                  (canAddSamples = savedBatchId && !viewOnly). Hidden in view mode
                  and the create flow. (user 2026-07-11) */}
              {canAddSamples && (
                <button className="ca-btn"
                  onClick={() => setModal('addsample')}><i className="ti ti-plus" /> Add Samples</button>
              )}
              {/* Rollback — HIDDEN entirely during a new-batch create (no existing
                  batch); shown (enabled only for status >= 2) once an existing batch
                  is open. Was previously always rendered but disabled. (user 2026-07-11) */}
              {existingHeader && (
                <button className="ca-btn" disabled={!canRollback}
                  title={canRollback ? '' : 'Available for a validated batch (Pending Validation onward)'}
                  onClick={rollbackBatch}><i className="ti ti-arrow-back-up" /> Rollback</button>
              )}
              <button className="ca-btn" disabled={!canCopy}
                title={canCopy ? '' : viewOnly ? 'Click Edit to make changes' : 'Load samples first'}
                onClick={() => setModal('copy')}><i className="ti ti-copy" /> Copy From Previous Batch</button>
            </>
          }
          right={
            <>
              {/* Reset · Load are CREATE-flow-only concepts (start a fresh batch /
                  pull pending samples into it) — neither applies once an EXISTING
                  batch is open (view or edit), so both are hidden entirely rather
                  than just disabled. marginLeft:auto pushes them to the far right
                  of the toolbar row (left-packed flex); Load stays the solid primary. */}
              {!existingHeader && (
                <button className="ca-btn" style={{ marginLeft: 'auto' }} onClick={resetBatch}>
                  <i className="ti ti-rotate" /> Reset</button>
              )}
              {!existingHeader && (
                <button className="ca-btn ca-btn-solid" disabled={!loadEnabled || sorted} onClick={() => loadSamples()}>
                  <span aria-hidden="true">⬇️</span>&nbsp;Load</button>
              )}
            </>
          }
        />
      </div>

      {/* detail card */}
      {/* Clicking anywhere in the card dismisses an open right-click context menu
          or picker popup — this div stops the click from reaching the root's
          own dismiss handler, so it must dismiss them itself. Controls that
          need to keep their own click (checkboxes, inputs, the popups
          themselves) already stopPropagation individually. */}
      <div className="card detailcard" onClick={e => { e.stopPropagation(); setOpenPop(null); closeCtx(); }}>
        <div className="cardhd">
          <div className="ttl"><span className="bar" /> General Information</div>
          <div className="sp" />
          <button className="iconbtn" title="Collapse / expand details" onClick={() => setGiCollapsed(v => !v)}>{giCollapsed ? '▾' : '▴'}</button>
          <button className="iconbtn" title="Hide / show QC Type panel" onClick={() => setQcCollapsed(v => !v)}>◧</button>
          <button className="iconbtn" title="Full screen grid" onClick={() => setFullGrid(v => !v)}>{fullGrid ? '⤡' : '⛶'}</button>
        </div>

        <div className="geninfo">
          <div className="form-grid">
            <Fld label="QC Batch ID:"><input value={existingHeader?.qcBatchId ?? 'Autogenerate'} readOnly /></Fld>
            {/* Pick-mode (OG New-from-list): Test is a lookup gated on the
                Matrix pick (legacy TestDataSource :680); otherwise read-only
                (seeded from the Analysis Queue / existing batch). */}
            {pickMode ? (
              <PickerFld label="Test:" req open={openPop === 'test'} disabled={sorted || !pickMatrix}
                onToggle={() => setOpenPop(openPop === 'test' ? null : 'test')}
                text={effTest ?? 'Select test…'} placeholder={!effTest}>
                {openPop === 'test' && (
                  <div className="pop">
                    <div className="popscroll"><table>
                      <thead><tr><th>Test</th><th>Method</th></tr></thead>
                      <tbody>
                        {testList.length === 0 && <tr><td colSpan={2} className="nodata">No tests with pending samples for this matrix.</td></tr>}
                        {testList.map(t => (
                          <tr key={t.testMethodId} className={pickTest?.testMethodId === t.testMethodId ? 'on' : ''}
                            onClick={() => void onPickTest(t)}>
                            <td>{t.testName}</td><td>{t.methodNumber ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                    <div className="pfoot"><span>Page 1 of 1 ({testList.length} items)</span></div>
                  </div>
                )}
              </PickerFld>
            ) : (
              <Fld label="Test:" req><input value={effTest ?? ''} readOnly /></Fld>
            )}
            <Fld label="No. Runs:" yellow><input value={noRuns} disabled={sorted}
              onChange={e => onNoRunsChange(e.target.value)}
              onBlur={e => { let n = Math.floor(Number(e.target.value)); if (!(n >= 1)) n = 1; onNoRunsChange(String(n)); }} /></Fld>

            <Fld label="Date Created:"><input
              value={fmtLocalDate(existingHeader?.createdDate ?? new Date().toISOString())} readOnly /></Fld>
            {/* Pick-mode: Method lists the TestName+Matrix variants (legacy
                MethodDataSource :789); the pick is the DEFINITIVE TestMethod. */}
            {pickMode ? (
              <PickerFld label="Method:" req open={openPop === 'method'} disabled={sorted || !pickTest}
                onToggle={() => setOpenPop(openPop === 'method' ? null : 'method')}
                text={effMethod ?? 'Select method…'} placeholder={!effMethod}>
                {openPop === 'method' && (
                  <div className="pop">
                    <div className="popscroll"><table>
                      <thead><tr><th>Method</th><th>Test</th></tr></thead>
                      <tbody>
                        {methodList.length === 0 && <tr><td colSpan={2} className="nodata">No method variants with pending samples.</td></tr>}
                        {methodList.map(m => (
                          <tr key={m.testMethodId} className={effTestMethodId === m.testMethodId ? 'on' : ''}
                            onClick={() => onPickMethod(m)}>
                            <td>{m.methodNumber ?? '—'}</td><td>{m.testName}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                    <div className="pfoot"><span>Page 1 of 1 ({methodList.length} items)</span></div>
                  </div>
                )}
              </PickerFld>
            ) : (
              <Fld label="Method:" req yellow><input value={effMethod ?? ''} readOnly /></Fld>
            )}
            <Fld label="Room Temp (°C):" yellow><input value={roomTemp} disabled={!roomHumidityEditable} onChange={e => setRoomTemp(e.target.value)} /></Fld>

            <Fld label="Created By:" req yellow><input value={existingHeader?.createdByName ?? 'Service'} readOnly /></Fld>
            <PickerFld label="Job ID:" req open={openPop === 'job'} disabled={sorted}
              onToggle={() => setOpenPop(openPop === 'job' ? null : 'job')}
              text={jobLabel} placeholder={!jobOids.length}>
              {openPop === 'job' && (
                <div className="pop">
                  <div className="popscroll"><table>
                    <thead><tr><th className="cbcol">#</th><th>Job ID</th><th className="num">Sx</th><th>TAT</th><th>Due Date</th><th>Date Received</th></tr></thead>
                    <tbody>
                      {jobsList.length === 0 && <tr><td colSpan={6} className="nodata">No jobs with pending samples.</td></tr>}
                      {jobsList.map(j => (
                        <tr key={j.jobOid} className={jobOids.includes(j.jobOid) ? 'on' : ''} onClick={() => toggleJob(j.jobOid)}>
                          <td className="cbcol"><input type="checkbox" className="chk" readOnly checked={jobOids.includes(j.jobOid)} /></td>
                          <td>{j.jobID}</td>
                          <td className="num">{j.sampleCount}</td>
                          <td>{j.tat ?? ''}</td>
                          <td>{fmtDate(j.dueDate)}</td>
                          <td>{fmtDate(j.receivedDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                  <div className="pfoot"><span>Page 1 of 1 ({jobsList.length} items)</span>
                    <button className="apply" onClick={() => setOpenPop(null)}>Apply</button></div>
                </div>
              )}
            </PickerFld>
            <Fld label="Humidity(%):" yellow><input value={humidity} disabled={!roomHumidityEditable} onChange={e => setHumidity(e.target.value)} /></Fld>

            {/* Pick-mode: Matrix is the FIRST pick of the cascade (legacy
                MatrixDataSource :635 — admin sees all, else the user's
                AnalysisDepartmentChain matrices). */}
            {pickMode ? (
              <PickerFld label="Matrix:" req open={openPop === 'matrix'} disabled={sorted}
                onToggle={() => setOpenPop(openPop === 'matrix' ? null : 'matrix')}
                text={effMatrix ?? 'Select matrix…'} placeholder={!effMatrix}>
                {openPop === 'matrix' && (
                  <div className="pop">
                    <div className="popscroll"><table>
                      <thead><tr><th>Matrix</th></tr></thead>
                      <tbody>
                        {matrixList.length === 0 && <tr><td className="nodata">No matrices available.</td></tr>}
                        {matrixList.map(m => (
                          <tr key={m.matrixOid} className={pickMatrix?.matrixOid === m.matrixOid ? 'on' : ''}
                            onClick={() => void onPickMatrix(m)}>
                            <td>{m.matrixName}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                    <div className="pfoot"><span>Page 1 of 1 ({matrixList.length} items)</span></div>
                  </div>
                )}
              </PickerFld>
            ) : (
              <Fld label="Matrix:" req yellow><input value={effMatrix ?? ''} readOnly /></Fld>
            )}
            <PickerFld label="Instrument:" req open={openPop === 'instr'} disabled={sorted}
              onToggle={() => setOpenPop(openPop === 'instr' ? null : 'instr')}
              text={instrLabel} placeholder={!instrument}>
              {openPop === 'instr' && (
                <div className="pop">
                  <div className="popscroll"><table>
                    <thead><tr><th className="cbcol">#</th><th>InstrumentNumber</th><th>InstrumentName</th></tr></thead>
                    <tbody>
                      {instrList.length === 0 && <tr><td colSpan={3} className="nodata">No instruments linked to this test.</td></tr>}
                      {instrList.map(ins => (
                        <tr key={ins.instrumentOid} className={instrument === ins.instrumentOid ? 'on' : ''} onClick={() => pickInstr(ins.instrumentOid)}>
                          <td className="cbcol"><input type="checkbox" className="chk" readOnly checked={instrument === ins.instrumentOid} /></td>
                          <td>{ins.labwareName}</td>
                          <td>{ins.instrumentName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                  <div className="pfoot"><span>Page 1 of 1 ({instrList.length} items)</span>
                    <button className="apply" onClick={() => setOpenPop(null)}>Apply</button></div>
                </div>
              )}
            </PickerFld>
            <PickerFld label="Template ID:" open={openPop === 'tpl'} disabled={sorted}
              onToggle={() => setOpenPop(openPop === 'tpl' ? null : 'tpl')}
              text={tplId == null ? 'Select template…' : String(tplId)}
              placeholder={tplId == null}>
              {openPop === 'tpl' && (
                <div className="pop">
                  <div className="popscroll"><table>
                    <thead><tr><th className="cbcol">#</th><th className="num">TemplateID</th><th>TemplateName</th></tr></thead>
                    <tbody>
                      {tplList.length === 0 && <tr><td colSpan={3} className="nodata">No templates wired for this test.</td></tr>}
                      {tplList.map(t => (
                        <tr key={t.templateID} className={tplId === t.templateID ? 'on' : ''}
                            onClick={() => {
                              setTplId(prev => prev === t.templateID ? null : t.templateID);
                              if (loaded) { setSorted(false); }   // template drives Sort sequence-pattern; force re-Sort
                            }}>
                          <td className="cbcol"><input type="checkbox" className="chk" readOnly checked={tplId === t.templateID} /></td>
                          <td className="num">{t.templateID}</td>
                          <td>{t.templateName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                  <div className="pfoot"><span>Page 1 of 1 ({tplList.length} items)</span>
                    <button className="apply" onClick={() => setOpenPop(null)}>Apply</button></div>
                </div>
              )}
            </PickerFld>

            <PickerFld label="Prep Batch ID:" open={openPop === 'prep'} disabled={sorted}
              onToggle={() => setOpenPop(openPop === 'prep' ? null : 'prep')}
              text={prepBatch} placeholder={false}>
              {openPop === 'prep' && (
                <div className="pop">
                  <div className="popscroll"><table>
                    <thead><tr><th className="cbcol">#</th><th>PrepBatchID</th><th>Datecreated</th><th>CreatedBy</th></tr></thead>
                    <tbody>
                      <tr className={prepBatch === 'N/A' ? 'on' : ''} onClick={() => pickPrep('N/A')}>
                        <td className="cbcol"><input type="radio" readOnly checked={prepBatch === 'N/A'} /></td>
                        <td>N/A</td><td /><td />
                      </tr>
                      {prepList.map(b => (
                        <tr key={b.prepBatchOid} className={prepBatch === (b.prepBatchID ?? '') ? 'on' : ''} onClick={() => pickPrep(b.prepBatchID ?? '')}>
                          <td className="cbcol"><input type="radio" readOnly checked={prepBatch === (b.prepBatchID ?? '')} /></td>
                          <td>{b.prepBatchID}</td><td>{fmtDate(b.createdDate)}</td><td>{b.createdBy}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                  <div className="pfoot"><span>Page 1 of 1 ({prepList.length} items)</span>
                    <button className="apply" onClick={() => setOpenPop(null)}>Apply</button></div>
                </div>
              )}
            </PickerFld>
          </div>
        </div>

        {/* tabs */}
        <div className="tabsrow">
          <div className="tabs">
            <div className={`tab ${tab === 'seq' ? 'active' : ''}`} onClick={() => setTab('seq')}>Batch Sequence</div>
            <div className={`tab ${tab === 'others' ? 'active' : ''}`} onClick={() => setTab('others')}>Others</div>
          </div>
          <div className="gridctl">
            <button className="btn" onClick={() => setGiCollapsed(v => !v)}>{giCollapsed ? '▾ Show details' : '▴ Collapse details'}</button>
            <button className="btn" onClick={() => setFullGrid(v => !v)}>{fullGrid ? '⤡ Exit full screen' : '⛶ Full screen'}</button>
          </div>
        </div>

        {tab === 'seq' ? (
          <div className="bseq">
            <div className="qcpanel">
              <div className="qh"><span>QCType</span><span>▲</span></div>
              {/* The QC-Type list is populated after Load (legacy parity).  The
                  green ＋ Add button appears only AFTER Sort — Add / Remove / Drag
                  are post-Sort actions; the type NAMES stay visible pre-Sort. The
                  ＋ button is always clickable (legacy grid parity: the Action
                  itself isn't selection-gated) — addQc() validates the checked
                  row count on click (exactly one required, QCadd_Execute :11264).
                  panelVisible = legacy TestMethod.QCTypes containment (:6601) —
                  types outside it exist for builder lookups but never list here. */}
              <div className="qlist">
                {loaded ? qcTypeList.filter(q => q.panelVisible).map(q => (
                  <div className="qitem" key={q.qcTypeOid}>
                    <span>{q.qcTypeName}</span>
                    {sorted && !viewOnly && <button className="circ add" title={`Add ${q.qcTypeName}`}
                      onClick={() => addQc(q)}>＋</button>}
                  </div>
                )) : (
                  <div className="nodata" style={{ padding: 18 }}>Load samples to see QC types.</div>
                )}
              </div>
              <div className="qcount">Count={loaded ? qcTypeList.filter(q => q.panelVisible).length : 0}</div>
            </div>
            <div className={`seqwrap ${sorted ? '' : 'presort'} ${viewOnly ? 'viewonly' : ''}`}>
              {/* Grid toolbar strip — OG has the XAF default "Export to" dropdown
                  here, directly above the sequence grid's column headers. */}
              <div className="seqgridbar">
                <ExportMenu filename={`qc-batch-sequence${existingHeader?.qcBatchId ? `-${existingHeader.qcBatchId}` : ''}`}
                            headers={seqExport.headers} rows={seqExport.rows} />
              </div>
              <div className="stscroll">
                <table>
                  {/* Pin the 9 frozen leading columns to exact widths via <col>
                      (auto layout honours a column-level width even for the narrow
                      sticky Sort cell, where a cell min-width can be ignored and
                      collapse on scroll). The data columns get NO <col> →
                      content-sized. Same freeze style as the prep grid. */}
                  <colgroup>
                    <col className="c-drag" />
                    <col className="c-cb" />
                    <col className="c-rm" />
                    <col className="c-qct" />
                    <col className="c-sort" />
                    <col className="c-sys" />
                    <col className="c-job" />
                    <col className="c-cmp" />
                    <col className="c-sid" />
                  </colgroup>
                  <thead>
                    {/* Column order matches the legacy XAF QC Batch loaded-samples
                        grid.  Editable cells (light-green tint in legacy): Sort,
                        DilutionCount, SampleAmount, FinalVol(ml), ExcludedReport.
                        See QCBatchController.cs:5011 for the editable allow-list. */}
                    <tr>
                      <th className="cbcol frz fz0" />
                      <th className="cbcol frz fz1"><input type="checkbox" className="chk" checked={allChecked}
                        disabled={sorted && viewOnly}
                        onChange={e => (sorted ? toggleAllChecked(e.target.checked) : toggleAllSeq(e.target.checked))} /></th>
                      <th className="cbcol frz fz2" />
                      <th className="frz fz3">QCType</th>
                      <th className="num frz fz4">Sort</th>
                      <th className="frz fz5">SysSampleCode</th>
                      <th className="frz fz6">JobID</th>
                      <th className="frz fz7">Component</th>
                      <th className="frz fz8">SampleID</th>
                      {/* Pre-Sort: editable DilutionCount (longer caption, needs the
                          full col-num-sm width). Post-Sort: "DF" — much shorter, its
                          own narrower col-df width. */}
                      {/* Non-frozen DATA columns — array-driven from the column model
                          so Hide / Column Chooser drop them from BOTH header and body.
                          Each carries the shared .adg-resize handle (position:relative
                          anchors it; the th isn't sticky). DupSource/SpikeSource keep
                          their .col-dupspike class so they still collapse to 0 width
                          pre-Sort via CSS; the column set is otherwise constant. */}
                      {seqCm.visibleColumns.map(c => (
                        <th key={c.key}
                            className={seqHClass[c.key] ?? ''}
                            style={{ position: 'relative', ...seqW(c.key) }}
                            onContextMenu={(e) => seqMenu.openHeaderMenu(e, { field: c.key, label: c.label, sortable: false })}>
                          {c.label}
                          <span className="adg-resize" onMouseDown={(e) => seqCm.startResize(c.key, e)} onClick={(e) => e.stopPropagation()} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td colSpan={9 + seqCm.visibleColumns.length} className="nodata">No data to display — pick Job ID + Instrument and click <b>Load</b>, then <b>Sort</b>.</td></tr>
                    ) : rows.map(r => (
                      <tr key={r.id}
                        className={`${rowClass(r)} ${r.checked ? 'sel' : ''} ${dropTarget === r.id ? 'dropbefore' : ''}`}
                        draggable={sorted && !viewOnly}
                        onClick={() => { if (sorted && !viewOnly) toggleSeqRowChecked(r.id); }}
                        onDragStart={() => { if (sorted && !viewOnly) dragId.current = r.id; }}
                        onDragOver={e => { if (!sorted || viewOnly) return; e.preventDefault(); if (r.id !== dragId.current) setDropTarget(r.id); }}
                        onDrop={e => { if (!sorted || viewOnly) return; e.preventDefault(); onDrop(r.id); }}
                        onDragEnd={() => setDropTarget(null)}
                        onContextMenu={e => {
                          e.preventDefault();
                          if (sorted && viewOnly) return;   // nothing mutating to offer in view mode
                          setCtx({ x: Math.min(e.clientX, window.innerWidth - 190), y: Math.min(e.clientY, window.innerHeight - 120), row: r });
                        }}>
                        {/* Drag-to-reorder handle appears only AFTER Sort — pre-Sort
                            the run order is driven by the checkboxes, not dragging. */}
                        <td className="drag frz fz0" title={sorted && !viewOnly ? 'Drag to reorder' : ''}>{sorted && !viewOnly ? '⠿' : ''}</td>
                        <td className="cbcol frz fz1"><input type="checkbox" className="chk"
                          checked={r.checked}
                          disabled={sorted && viewOnly}
                          title={sorted ? 'Select for Add QC' : ''}
                          onClick={e => e.stopPropagation()}
                          onChange={() => {
                            // Pre-Sort the checkbox drives the legacy click-order Sort
                            // numbering (toggleSeqRow).  Post-Sort it's a plain
                            // multi-select grid checkbox (legacy grid selection
                            // parity, QCBatchController.cs QCadd_Execute :11264) — Add
                            // QC validates "exactly one checked" on click, not here.
                            if (sorted) toggleSeqRowChecked(r.id);
                            else toggleSeqRow(r.id);
                          }} /></td>
                        <td className="cbcol frz fz2">{sorted && !viewOnly && <button className="circ rm" title="Remove"
                          onClick={e => { e.stopPropagation(); void removeRow(r.id); }}>−</button>}</td>
                        <td className="qct frz fz3">{r.qc}</td>
                        {/* Post-Sort the Sort column is READ-ONLY — its value is
                            driven solely by drag-drop reorder (legacy: Sort editing is
                            cancelled once the remove column is visible,
                            QCBatchController.cs:3259-3270).  Pre-Sort it stays an
                            editable input (checkbox click-order + manual entry). */}
                        {sorted ? (
                          <td className="num frz fz4">{r.sort ?? ''}</td>
                        ) : (
                          <td className="num edcell frz fz4">
                            <input className="cellinp num" type="number"
                              value={r.sort ?? ''}
                              onChange={e => updateRow(r.id, 'sort', e.target.value === '' ? null : Math.max(0, Number(e.target.value)))} />
                          </td>
                        )}
                        <td className="frz fz5">{r.code || ''}</td>
                        <td className="frz fz6">{r.job || ''}</td>
                        <td className="frz fz7">{r.component || ''}</td>
                        <td className="frz fz8">{r.sid || ''}</td>
                        {/* Pre-Sort: editable DilutionCount input (bound to dilutionCount).
                            Post-Sort: editable DF input (the dilution factor, default 1) —
                            same transient-empty-while-typing / clamp-on-blur pattern. */}
                        {/* onClick stopPropagation on every editable control below:
                            the <tr> itself toggles row selection on click (post-Sort),
                            so without this, clicking into a cell to edit it would
                            bubble up and immediately un-check the row being edited. */}
                        {(() => {
                          // Body cells for the non-frozen DATA columns, keyed by id —
                          // same set + order as the header (visibleColumns) so a Hide
                          // drops the cell from both. seqW() applies a dragged width
                          // over the fixed CSS min/max. DupSource/SpikeSource still
                          // collapse pre-Sort via .col-dupspike; the values are blank
                          // pre-Sort (dup/spike links only exist once the sequence is
                          // built). Editable cells keep their stopPropagation so a
                          // click to edit doesn't bubble to the row-select handler.
                          const cell: Record<string, ReactNode> = {
                            dilution: sorted ? (
                              <td key="dilution" className="num edcell col-df" style={seqW('dilution')}>
                                <input className="cellinp num" type="number" min={1}
                                  value={r.df || ''}
                                  disabled={viewOnly}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => updateRow(r.id, 'df', e.target.value === '' ? 0 : Math.max(0, Number(e.target.value) || 0))}
                                  onBlur={e => updateRow(r.id, 'df', Math.max(1, Number(e.target.value) || 1))} />
                              </td>
                            ) : (
                              <td key="dilution" className="num edcell col-num-sm" style={seqW('dilution')}>
                                {/* transient-empty while typing (0 sentinel, shown blank); clamp >=1 on blur */}
                                <input className="cellinp num" type="number" min={1}
                                  value={r.dilutionCount || ''}
                                  onChange={e => updateRow(r.id, 'dilutionCount', e.target.value === '' ? 0 : Math.max(0, Number(e.target.value) || 0))}
                                  onBlur={e => updateRow(r.id, 'dilutionCount', Math.max(1, Number(e.target.value) || 1))} />
                              </td>
                            ),
                            sampleAmount: (
                              <td key="sampleAmount" className="num edcell col-num-sm" style={seqW('sampleAmount')}>
                                <input className="cellinp num" type="text"
                                  value={r.sampleAmount} disabled={viewOnly}
                                  onClick={e => e.stopPropagation()}
                                  onFocus={() => setFocusedCol('sampleAmount')}
                                  onChange={e => updateRow(r.id, 'sampleAmount', e.target.value)} />
                              </td>
                            ),
                            finalVol: (
                              <td key="finalVol" className="num edcell col-num-sm" style={seqW('finalVol')}>
                                <input className="cellinp num" type="text"
                                  value={r.finalVol} disabled={viewOnly}
                                  onClick={e => e.stopPropagation()}
                                  onFocus={() => setFocusedCol('finalVol')}
                                  onChange={e => updateRow(r.id, 'finalVol', e.target.value)} />
                              </td>
                            ),
                            // Dup/Spike SOURCE — a selection combo on rows that have valid
                            // candidates, plain text otherwise (legacy cancels the editor
                            // when a row's option list is empty).  Pre-Sort the columns are
                            // collapsed to 0 width and the values blank, so keep them text.
                            // A dropdown ONLY when there is a real choice — 2+ candidates
                            // (user 2026-08-07). One candidate means there is nothing to
                            // pick, so it renders as text; and no blank entry is offered.
                            dupSource: (
                              <td key="dupSource" className="col-dupspike edcell" style={seqW('dupSource')}>
                                {sorted && !viewOnly && (sourceOpts.get(r.id)?.dup.length ?? 0) > 1
                                  ? <select className="cellinp" value={r.dupSource || ''}
                                      onClick={e => e.stopPropagation()}
                                      onChange={e => updateRow(r.id, 'dupSource', e.target.value)}>
                                      {sourceOpts.get(r.id)!.dup.map(o => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                  : (sorted ? (r.dupSource || '') : '')}
                              </td>
                            ),
                            spikeSource: (
                              <td key="spikeSource" className="col-dupspike edcell" style={seqW('spikeSource')}>
                                {sorted && !viewOnly && (sourceOpts.get(r.id)?.spike.length ?? 0) > 1
                                  ? <select className="cellinp" value={r.spikeSource || ''}
                                      onClick={e => e.stopPropagation()}
                                      onChange={e => updateRow(r.id, 'spikeSource', e.target.value)}>
                                      {sourceOpts.get(r.id)!.spike.map(o => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                  : (sorted ? (r.spikeSource || '') : '')}
                              </td>
                            ),
                            sampleName:     <td key="sampleName" style={seqW('sampleName')}>{r.sampleName || ''}</td>,
                            siteName:       <td key="siteName" style={seqW('siteName')}>{r.siteName || ''}</td>,
                            samplingAddress:<td key="samplingAddress" style={seqW('samplingAddress')}>{r.samplingAddress || ''}</td>,
                            units:          <td key="units" style={seqW('units')}>{r.units || ''}</td>,
                            holdTime:       <td key="holdTime" style={seqW('holdTime')}>{r.holdTime || ''}</td>,
                            dateCreated:    <td key="dateCreated" style={seqW('dateCreated')}>{fmtLocalShort(r.dateCreated)}</td>,
                            dateReceived:   <td key="dateReceived" style={seqW('dateReceived')}>{fmtLocalShort(r.dateReceived)}</td>,
                            dateCollected:  <td key="dateCollected" style={seqW('dateCollected')}>{fmtLocalShort(r.dateCollected)}</td>,
                            client:         <td key="client" style={seqW('client')}>{r.client || ''}</td>,
                            projectId:      <td key="projectId" style={seqW('projectId')}>{r.projectId || ''}</td>,
                            projectName:    <td key="projectName" style={seqW('projectName')}>{r.projectName || ''}</td>,
                            excludedReport: (
                              <td key="excludedReport" className="cbcol edcell" style={seqW('excludedReport')}>
                                <input type="checkbox" className="chk"
                                  checked={r.excludedReport} disabled={viewOnly}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => void onToggleExcludedReport(r, e.target.checked)} />
                              </td>
                            ),
                          };
                          return seqCm.visibleColumns.map(c => cell[c.key]);
                        })()}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Header right-click menu (Hide · Show All · Column Chooser) +
                    the Column Chooser popup — both portal to <body>, rendered once. */}
                {seqMenu.headerMenu}
                {seqCm.chooser}
              </div>
              <div className="qcount">Count={rows.length}</div>
            </div>
          </div>
        ) : (
          <div className="otherwrap">
            <OtherSection title="Reagent"
              onAdd={() => setModal('reagent')}
              onRemove={unlinkSelectedReagents}
              disabled={viewOnly}
              count={savedBatchId ? reagentRows.length : stagedReagents.length}
              head={<tr><th className="cbcol"><input type="checkbox" className="chk" checked={allReagentChecked} onChange={e => toggleAllReagentSel(e.target.checked)} /></th><th>ReagentID</th><th>ReagentName</th><th>ExpiredDate</th><th>DatePrepared</th><th>PreparedBy</th><th>COA</th><th>DateReceived</th><th>Vendor</th></tr>}
              body={savedBatchId ? (
                reagentRows.length === 0
                  ? <tr><td colSpan={9} className="onodata">No reagents added.</td></tr>
                  : reagentRows.map(r => (
                    <tr key={r.oid} onClick={() => toggleReagentSel(r.oid)}>
                      <td className="cbcol"><input type="checkbox" className="chk" readOnly checked={reagentSel.has(r.oid)} onClick={e => e.stopPropagation()} onChange={() => toggleReagentSel(r.oid)} /></td>
                      <td>{r.reagentID}</td><td>{r.reagentName}</td>
                      <td>{fmtLocalDate(r.expiredDate)}</td><td>{fmtLocalShort(r.datePrepared)}</td>
                      <td>{r.preparedBy}</td>
                      {/* COA link only when the reagent actually has an uploaded
                          certificate on file (server-computed hasCoa) — legacy hides
                          the COA button otherwise (Grid_HtmlCommandCellPrepared :4958). */}
                      <td>{r.hasCoa &&
                        <button className="coa-link" onClick={e => { e.stopPropagation(); void openCoa(r.reagentID); }}>COA</button>}</td>
                      <td>{fmtLocalShort(r.dateReceived)}</td><td>{r.vendorName}</td>
                    </tr>
                  ))
              ) : (
                stagedReagents.length === 0
                  ? <tr><td colSpan={9} className="onodata">No reagents added.</td></tr>
                  : stagedReagents.map(r => (
                    <tr key={r.reagentID} onClick={() => toggleStagedReagentSel(r.reagentID ?? '')}>
                      <td className="cbcol"><input type="checkbox" className="chk" readOnly checked={stagedReagentSel.has(r.reagentID ?? '')} onClick={e => e.stopPropagation()} onChange={() => toggleStagedReagentSel(r.reagentID ?? '')} /></td>
                      <td>{r.reagentID}</td><td>{r.reagentName}</td>
                      <td>{fmtLocalDate(r.expiryDate)}</td><td>{fmtLocalShort(r.datePrepared)}</td>
                      <td>{r.preparedBy}</td>
                      {/* Staged (pre-save) reagents have no server-computed COA flag
                          yet — the COA link appears once the batch is saved and the
                          grid switches to the linked reagentRows (with hasCoa). */}
                      <td />
                      <td>{fmtLocalShort(r.dateReceived)}</td><td>{r.vendor}</td>
                    </tr>
                  ))
              )} />
            <OtherSection title="Instrument"
              onAdd={() => setModal('instr2')}
              onRemove={unlinkSelectedInstruments}
              disabled={viewOnly}
              count={savedBatchId ? instrumentRows.length : stagedInstruments.length}
              head={<tr><th className="cbcol"><input type="checkbox" className="chk" checked={allInstrumentChecked} onChange={e => toggleAllInstrumentSel(e.target.checked)} /></th><th>InstrumentNumber</th><th>InstrumentName</th><th>Manufacturer</th><th>ExpirationDate</th><th>DepartmentUsed</th><th>Active</th><th>MaintenanceFrequency</th><th>Department</th><th>Category</th><th>RetiredDate</th></tr>}
              body={savedBatchId ? (
                instrumentRows.length === 0
                  ? <tr><td colSpan={11} className="onodata">No instruments added.</td></tr>
                  : instrumentRows.map(r => (
                    <tr key={r.instrumentOid} onClick={() => toggleInstrumentSel(r.instrumentOid)}>
                      <td className="cbcol"><input type="checkbox" className="chk" readOnly checked={instrumentSel.has(r.instrumentOid)} onClick={e => e.stopPropagation()} onChange={() => toggleInstrumentSel(r.instrumentOid)} /></td>
                      <td>{r.labwareName}</td><td>{r.instrumentName}</td>
                      <td>{r.manufacturer}</td><td>{fmtLocalDate(r.expirationDate)}</td>
                      <td>{r.departmentUsedName}</td><td>{r.active === undefined ? '' : (r.active ? 'Yes' : 'No')}</td>
                      <td>{r.maintenanceFrequency}</td><td>{r.departmentNames}</td>
                      <td>{r.categoryName}</td><td>{fmtLocalDate(r.retiredDate)}</td>
                    </tr>
                  ))
              ) : (
                stagedInstruments.length === 0
                  ? <tr><td colSpan={11} className="onodata">No instruments added.</td></tr>
                  : stagedInstruments.map(r => (
                    <tr key={r.instrumentOid} onClick={() => toggleStagedInstrumentSel(r.instrumentOid)}>
                      <td className="cbcol"><input type="checkbox" className="chk" readOnly checked={stagedInstrumentSel.has(r.instrumentOid)} onClick={e => e.stopPropagation()} onChange={() => toggleStagedInstrumentSel(r.instrumentOid)} /></td>
                      <td>{r.labwareName}</td><td>{r.instrumentName}</td>
                      <td>{r.manufacturer}</td><td>{fmtLocalDate(r.expirationDate)}</td>
                      <td>{r.departmentUsedName}</td><td>{r.active === undefined ? '' : (r.active ? 'Yes' : 'No')}</td>
                      <td>{r.maintenanceFrequency}</td><td>{r.departmentNames}</td>
                      <td>{r.categoryName}</td><td>{fmtLocalDate(r.retiredDate)}</td>
                    </tr>
                  ))
              )} />
            <div className="osect">
              <h3>Comment</h3>
              {/* Header Comment — legacy SpreadSheetEntry_AnalyticalBatch.Comments is a
                  PLAIN nvarchar(max) memo (no RTF), so a plain textarea keeps AI's stored
                  value byte-compatible with the legacy XAF app.  Persisted with the batch
                  on Create + Save (not a per-action save).  Read-only in view mode. */}
              <div className="commentbox">
                <textarea className="editor" value={comment} disabled={viewOnly}
                  placeholder={viewOnly ? '' : 'Add a comment for this batch…'}
                  onChange={e => setComment(e.target.value)} />
              </div>
            </div>
          </div>
        )}

        <div className="detail-actions">
          {/* Previous steps back from the built sequence to sample SELECTION —
              meaningless for an existing batch (its loadedSamples were never
              populated; the app never ran the manual Load step for it), so
              hidden entirely rather than just disabled, same as Reset/Load above. */}
          {!existingHeader && <button className="btn" disabled={!sorted} onClick={goPrevious}>‹ Previous</button>}
          {/* Sort shows before the sequence is built; OK shows only after. Use
              Previous to step back from the built sequence to sample selection. */}
          {!sorted && <button className={`btn ${anySample ? 'primary' : ''}`} disabled={!anySample} onClick={buildSequence}>Sort ⇅</button>}
          {/* OK is the CREATE action — hidden once the batch already has an
              abOid, whether from a just-completed Create or from opening an
              EXISTING batch (savedBatchId is set immediately on open, see the
              detail-load effect above), so it's never possible to re-create /
              duplicate an already-saved batch. */}
          {sorted && !savedBatchId && !viewOnly && <button className="btn primary" onClick={onOk}>OK</button>}
          {/* Save is the EDIT-mode persist action (A1) — shown only when an existing,
              still-editable (Pending Submission) batch is open in edit mode. Reconciles
              the current grid + Room Temp/Humidity back to the saved batch. */}
          {existingHeader && !viewOnly && batchIsEditable &&
            <button className="btn primary" onClick={saveEdits} disabled={!editDirty}
              title={editDirty ? undefined : 'No changes to save'}>OK</button>}
        </div>
      </div>

      {/* context menu — only rendered when it has at least one applicable item.
          Add Dilution no longer depends on which row was right-clicked — its
          target is whichever row is CHECKED (validated inside addDilution());
          the menu item itself is always offered post-Sort, same as the ＋
          Add QC button. */}
      {ctx && (focusedCol || sorted) && (
        <div className="qcb-ctxmenu" style={{ left: ctx.x, top: ctx.y }} onClick={e => e.stopPropagation()}>
          {/* Copy To All Cell — copies this row's value for the last-focused editable
              column (SampleAmount / FinalVol) to every SELECTED row; only shown when
              such a cell was focused (legacy allow-list, QCBatchController.cs:3286). */}
          {focusedCol && <div onClick={() => copyToAll(ctx.row)}>▦ Copy To All Cell</div>}
          {/* Add Dilution — post-Sort; addDilution() validates exactly one checked
              row and that it isn't itself a dilution row. */}
          {sorted && <div onClick={() => void addDilution()}>💧 Add Dilution</div>}
        </div>
      )}

      {/* modals — the Audit Trail is NOT here: the shared config-gated
          <AuditTrailButton> is auto-rendered by <AppToolbar auditFormName="QC Batches"
          auditSourceId={savedBatchId}> and shows the real audit rows Create /
          Rollback / edit write (confirmed live via GET /audit/trail). */}
      {modal === 'notes'  && jobOids[0] && <NotesModal jobOid={jobOids[0]} jobLabel={jobLabel} onClose={() => { setModal(null); void loadNotesCount(); }} />}
      {modal === 'copy'   && <CopyModal
        testMethodId={effTestMethodId}
        excludeAbOid={savedBatchId ?? undefined}
        onClose={() => setModal(null)}
        onApply={(src, reag, instr, comm) => { setModal(null); void copyFromPrevious(src, reag, instr, comm); }} />}
      {/* The picker opens PRE-SEEDED with whatever's already linked/staged on
          the batch (RIGHT panel) — LEFT panel is the catalog minus that set.
          OK returns the final staged list; reconcileInstruments/reconcileReagents
          compute the delta vs. preLinked and apply add + remove.  Matches the
          mockup's commitInstruments/commitReagents wholesale replacement. */}
      {modal === 'instr2' && <InstrPicker
        testMethodId={effTestMethodId || null}
        preLinked={savedBatchId ? instrumentRows : stagedInstruments}
        onClose={() => setModal(null)}
        onCommit={fin => { setModal(null); void reconcileInstruments(fin); }} />}
      {modal === 'reagent' && <ReagentPicker
        testMethodId={effTestMethodId || null}
        preLinked={
          savedBatchId
            // QcLinkedReagent has no `test` (not a persisted dbo.Reagent column, see the
            // StagedReagent comment above) — every other field is available.
            ? reagentRows.map(r => ({ oid: r.oid, reagentID: r.reagentID, reagentName: r.reagentName, prepType: r.prepType, expiryDate: r.expiredDate, test: null, datePrepared: r.datePrepared, preparedBy: r.preparedBy, dateReceived: r.dateReceived }))
            : stagedReagents.map(r => ({ reagentID: r.reagentID, reagentName: r.reagentName, prepType: r.prepType, expiryDate: r.expiryDate, test: r.test, datePrepared: r.datePrepared, preparedBy: r.preparedBy, dateReceived: r.dateReceived }))
        }
        onClose={() => setModal(null)}
        onCommit={fin => { setModal(null); void reconcileReagents(fin); }} />}
      {modal === 'addsample' && savedBatchId && <AddSamplePicker abOid={savedBatchId} onClose={() => setModal(null)}
        onCommit={sel => { setModal(null); void addSamples(sel); }} />}
      </div>
    </>
  );
}

// ── small field wrappers ─────────────────────────────────────────────────────
function Fld({ label, req, yellow, children }: { label: string; req?: boolean; yellow?: boolean; children: ReactNode }) {
  return (
    <div className="fld">
      <label>{label}{req && <span className="star">*</span>}</label>
      <div className={`ctl ${yellow ? 'yellow' : ''}`}>{children}</div>
    </div>
  );
}
function PickerFld({ label, req, open, onToggle, text, placeholder, disabled, children }:
  { label: string; req?: boolean; open: boolean; onToggle: () => void; text: string; placeholder: boolean; disabled?: boolean; children: ReactNode }) {
  return (
    <div className="fld pos" onClick={e => e.stopPropagation()}>
      <label>{label}{req && <span className="star">*</span>}</label>
      <div className="ctl">
        <div className={`picker ${req && placeholder ? 'req' : ''} ${disabled ? 'disabled' : ''}`}
          onClick={disabled ? undefined : onToggle}>
          <span className={placeholder ? 'ph' : ''}>{text}</span><span className="car">▾</span>
        </div>
        {open && !disabled && children}
      </div>
    </div>
  );
}

function OtherSection({ title, onAdd, onRemove, count, head, body, disabled }:
  { title: string; onAdd: () => void; onRemove: () => void; count: number; head: ReactNode; body: ReactNode; disabled?: boolean }) {
  return (
    <div className="osect">
      <h3>{title}</h3>
      <div className="subtb">
        <button onClick={onAdd} disabled={disabled}><span className="scirc g">＋</span> Add</button>
        <button onClick={onRemove} disabled={disabled}><span className="scirc r">−</span> Remove</button>
      </div>
      <div className="gridbox"><table><thead>{head}</thead><tbody>{body}</tbody></table></div>
      <div className="foot"><span>Page 1 of 1 ({count} items)</span>
        <div className="pages"><span className="pg">‹</span><b>1</b><span className="pg">›</span></div>
        <div className="psize">Page size: <select defaultValue="20"><option>20</option></select></div></div>
    </div>
  );
}

// ── modals ───────────────────────────────────────────────────────────────────

// Pull the server's OWN error message off an axios error (the API envelope
// `error.message`, e.g. "Cannot rollback as one or more samples are already
// reported." or the 403 author-only note message) so the user sees the real,
// OG-style reason — NOT axios's generic "Request failed with status code 400".
// Falls back to the caller's friendly text when there's no envelope (network error).
function apiErrMsg(e: unknown, fallback: string): string {
  const er = e as { response?: { data?: { error?: { message?: string } } }; isAxiosError?: boolean; message?: string };
  // 1. HTTP-error envelope (4xx/5xx) — the API's own error.message.
  const envelope = er.response?.data?.error?.message;
  if (envelope) return envelope;
  // 2. A CONTROLLED rejection: the API answers HTTP 200 with {success:false,error}
  //    and `unwrap` rethrows it as a plain Error carrying that message. Without
  //    this branch the real reason was swallowed and every such case surfaced as
  //    the generic fallback (e.g. Sort reporting "Failed to build the QC sequence."
  //    instead of the actual "Sample preparation process is required."). Excluded
  //    for axios errors so a bare transport failure still shows the friendly
  //    fallback rather than "Request failed with status code 500". (fix 2026-08-07)
  if (!er.response && !er.isAxiosError && typeof er.message === 'string' && er.message) return er.message;
  return fallback;
}

// Project Notes — legacy QC Batch "Comment" action (Comment_Execute :13203):
// a note linked to the JOB (Samplecheckin), both Title AND Text required
// (Dc_Accepting1), author-only edit + delete (CLAUDE.md §14a Rule C). Reuses
// the EXISTING shared dbo.Notes stack (Custom Reporting's job-scoped endpoints —
// the note store, author gate + audit FormName "Project Notes" are all shared,
// never reinvented); the reader's @Sources union already surfaces a batch's notes
// for its job. Keyed by the batch's first job Oid.
type ProjectNote = {
  oid: string; title: string | null; plainText: string | null; text: string | null;
  createdBy: string | null; createdDate: string | null; sort: number; canEdit: boolean;
};
function NotesModal({ jobOid, jobLabel, onClose }: { jobOid: string; jobLabel: string; onClose: () => void }) {
  const notify = useNotify();
  const base = `/api/v1/reporting/custom/${encodeURIComponent(jobOid)}/project-notes`;
  const [notes, setNotes]   = useState<ProjectNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]     = useState(false);
  // Add / edit share one form. editOid null = adding a new note.
  const [formOpen, setFormOpen] = useState(false);
  const [editOid, setEditOid]   = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [text, setText]   = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(base); setNotes((r.data as { data?: ProjectNote[] }).data ?? []); }
    catch { notify.error('Could not load project notes.'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobOid]);
  useEffect(() => { void load(); }, [load]);

  const openAdd  = () => { setEditOid(null); setTitle(''); setText(''); setFormOpen(true); };
  const openEdit = (n: ProjectNote) => {
    // Author-only gate (Rule C) — mirror of the server's 403, applied before the PUT.
    if (!n.canEdit) { notify.error('You are not authorized to modify a note created by another author.'); return; }
    setEditOid(n.oid); setTitle(n.title ?? ''); setText(n.plainText ?? n.text ?? ''); setFormOpen(true);
  };
  const save = async () => {
    // Both Title AND Text required — legacy Dc_Accepting1 + BO [RuleRequiredField].
    if (!title.trim() || !text.trim()) { notify.warning('Both Title and Note are required.'); return; }
    setBusy(true);
    try {
      if (editOid) {
        await api.put(`/api/v1/reporting/custom/notes/${encodeURIComponent(editOid)}`,
          { title: title.trim(), plainText: text.trim(), sort: 0, form: 'Project Notes' });
      } else {
        await api.post(base, { title: title.trim(), text: text.trim(), noteSource: 'QC Batch' });
      }
      setFormOpen(false);
      await load();
      qcMsg(editOid ? 'Note updated.' : 'Note added.', 'Project Notes');
    } catch (e) { notify.error(apiErrMsg(e, 'Saving the note failed.')); }
    finally { setBusy(false); }
  };
  const del = async (n: ProjectNote) => {
    if (!n.canEdit) { notify.error('You are not authorized to modify a note created by another author.'); return; }
    setBusy(true);
    try {
      await api.delete(`/api/v1/reporting/custom/project-notes/${encodeURIComponent(n.oid)}`);
      await load();
      qcMsg('Note deleted.', 'Project Notes');
    } catch (e) { notify.error(apiErrMsg(e, 'Deleting the note failed.')); }
    finally { setBusy(false); }
  };

  return (
    <Scrim onClose={onClose} width={880}>
      <div className="mh notes-mh">🗒 Project Notes — {jobLabel} <span className="x" onClick={onClose}>×</span></div>
      <div className="pb">
        <div style={{ marginBottom: 10 }}>
          <button className="btn primary" onClick={formOpen ? () => setFormOpen(false) : openAdd}>
            {formOpen ? 'Cancel' : '＋ Add Note'}
          </button>
        </div>
        {formOpen && (
          <div style={{ border: '1px solid var(--ai-line)', borderRadius: 6, padding: 12, marginBottom: 12, background: 'var(--ai-header-bg)' }}>
            <input style={{ width: '100%', marginBottom: 8, padding: '6px 9px', border: '1px solid var(--ai-line)', borderRadius: 6 }}
              placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} />
            <textarea style={{ width: '100%', minHeight: 80, padding: '6px 9px', border: '1px solid var(--ai-line)', borderRadius: 6 }}
              placeholder="Note" value={text} onChange={e => setText(e.target.value)} />
            <div style={{ marginTop: 8 }}><button className="btn primary" disabled={busy} onClick={save}>
              {editOid ? 'Update Note' : 'Save Note'}</button></div>
          </div>
        )}
        <div className="gridbox">
          <table><thead><tr><th>Title</th><th>Note</th><th>Author</th><th>Date</th><th /></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="onodata">Loading…</td></tr>
              ) : notes.length === 0 ? (
                <tr><td colSpan={5} className="onodata">No project notes for this job.</td></tr>
              ) : notes.map(n => (
                <tr key={n.oid}>
                  <td>{n.title}</td>
                  <td style={{ whiteSpace: 'normal' }}>{n.plainText ?? n.text}</td>
                  <td>{n.createdBy}</td>
                  <td>{fmtLocalShort(n.createdDate)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {n.canEdit && <>
                      <button className="btn" onClick={() => openEdit(n)}>Edit</button>{' '}
                      <button className="btn" disabled={busy} onClick={() => void del(n)}>Delete</button>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mf"><button className="btn" onClick={onClose}>Close</button></div>
    </Scrim>
  );
}

// Copy From Previous Batch — legacy SamplePreparationController copy-from-previous
// (QC branch, :6287-6353): pick a prior batch (same test method), tick which of
// its parts to copy — OG CopyFromSamplePrepBatch checkboxes Reagent · Instrument ·
// Others(Comment) · All the Above — OK → append + de-dup into this batch (handled
// by copyFromPrevious in the page). "All the Above" ticks/copies all three.
function CopyModal({ testMethodId, excludeAbOid, onClose, onApply }: {
  testMethodId: string; excludeAbOid?: string;
  onClose: () => void; onApply: (sourceAbOid: string, copyReagents: boolean, copyInstruments: boolean, copyOthers: boolean) => void;
}) {
  const notify = useNotify();
  const [list, setList]       = useState<QcPreviousBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel]         = useState<string | null>(null);
  // All checkboxes start UNCHECKED on open (user 2026-07-11; matches OG, where the
  // CopyFromSamplePrepBatch BO booleans default false). cpOthers = copy the Comment.
  const [cpReagent, setCpReagent] = useState(false);
  const [cpInstr, setCpInstr]     = useState(false);
  const [cpOthers, setCpOthers]   = useState(false);
  // "All the Above" — master checkbox: checked iff all three are, toggling it
  // sets all three (OG AllAbove → copyReagent = copyInstrument = copyOthers).
  const allAbove = cpReagent && cpInstr && cpOthers;
  const setAllAbove = (v: boolean) => { setCpReagent(v); setCpInstr(v); setCpOthers(v); };
  useEffect(() => {
    qcBatchApi.previousBatches(testMethodId, excludeAbOid)
      .then(setList)
      .catch(() => notify.error('Could not load previous batches.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testMethodId, excludeAbOid]);
  const ok = () => {
    if (!sel) { notify.warning('Select a QC batch to copy from.'); return; }          // legacy guard
    if (!cpReagent && !cpInstr && !cpOthers) { notify.warning('Select at least one item to copy.'); return; }  // legacy "selectchkbox"
    onApply(sel, cpReagent, cpInstr, cpOthers);
  };
  return (
    <Scrim onClose={onClose} width={760}>
      <div className="mh">📋 Copy From Previous Batch <span className="x" onClick={onClose}>×</span></div>
      <div className="pb">
        <div style={{ display: 'flex', gap: 20, marginBottom: 10, fontSize: 13 }}>
          <label><input type="checkbox" checked={cpReagent} onChange={e => setCpReagent(e.target.checked)} /> Reagent</label>
          <label><input type="checkbox" checked={cpInstr} onChange={e => setCpInstr(e.target.checked)} /> Instrument</label>
          <label><input type="checkbox" checked={cpOthers} onChange={e => setCpOthers(e.target.checked)} /> Others</label>
          <label><input type="checkbox" checked={allAbove} onChange={e => setAllAbove(e.target.checked)} /> All the Above</label>
        </div>
        <div className="gridbox">
          <table><thead><tr><th className="cbcol" /><th>QCBatchID</th><th>Matrix</th><th>Status</th><th>Date</th><th className="num">Reagents</th><th className="num">Instruments</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="onodata">Loading…</td></tr>
              ) : list.length === 0 ? (
                <tr><td colSpan={7} className="onodata">No previous batches for this test.</td></tr>
              ) : list.map(b => (
                <tr key={b.abOid} className={sel === b.abOid ? 'sel' : ''} style={{ cursor: 'pointer' }} onClick={() => setSel(b.abOid)}>
                  <td className="cbcol"><input type="radio" readOnly checked={sel === b.abOid} /></td>
                  <td>{b.qcBatchId}</td><td>{b.matrix}</td><td>{b.statusText}</td><td>{fmtLocalShort(b.createdDate)}</td>
                  <td className="num">{b.reagentCount}</td><td className="num">{b.instrumentCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mf"><button className="btn primary" onClick={ok}>OK</button><button className="btn" onClick={onClose}>Cancel</button></div>
    </Scrim>
  );
}
// Others-tab instrument picker — fetches the broader "AvailableLabwares" catalog
// (legacy parity, see AI_Analysis_QcBatch_Instruments.sql header), NOT the
// test-method-scoped list used by the single header Instrument field.
// Others-tab instrument picker — TRUE dual-panel design, ported verbatim from
// InstrPicker — QC-Batch-Prototype.html #instrPickScrim (renderInstrPicker2 /
// instrAddT / instrRemT / commitInstruments) parity. Both panels have the SAME
// 3 data columns: InstrumentID (Labware.LabwareID, e.g. "IN0040") · InstrumentName
// (Labware.AssignedName) · InstrumentNumber (Labware.LabwareName — legacy XAF
// caption swap, QCBatchController.cs :1633/:1655). Column widths are ~equal
// and cells wrap long values to a 2nd line (see .dualcol table CSS).
//
// SEMANTICS (mockup-parity, replaces the earlier additive-only picker): the
// RIGHT panel opens PRE-SEEDED with whatever's already linked/staged on the
// batch; the LEFT panel shows the catalog minus that set. The user can + a
// row (moves it right) or − a row (moves it back). OK returns the final
// staged list AND the original preLinked set so the caller can compute a
// delta (add + remove) — matching the mockup's commitInstruments which
// wholesale REPLACES instrumentRows with the picker's set.
function InstrPicker({ preLinked, testMethodId, onClose, onCommit }:
  { preLinked: QcInstrument[]; testMethodId?: string | null;
    onClose: () => void; onCommit: (final: QcInstrument[]) => void }) {
  const notify = useNotify();
  const [options, setOptions] = useState<QcInstrument[]>([]);
  const [loading, setLoading] = useState(true);
  // Staged (RIGHT panel) is initialised from preLinked so the user sees the
  // current batch state on open, matching mockup openInstrPicker2().
  const [staged, setStaged] = useState<QcInstrument[]>(preLinked);
  const [searchL, setSearchL] = useState('');
  const [searchR, setSearchR] = useState('');
  useEffect(() => {
    qcBatchApi.availableInstruments(testMethodId)
      .then(all => {
        // Merge: any preLinked row not in the catalog result (retired instrument
        // still linked to the batch, say) is added so it can still be displayed
        // and un-linked.
        const known = new Set(all.map(i => i.instrumentOid));
        const extra = preLinked.filter(i => !known.has(i.instrumentOid));
        setOptions([...all, ...extra]);
      })
      .catch(() => notify.error('Could not load the instrument catalog.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const stagedIds = useMemo(() => new Set(staged.map(s => s.instrumentOid)), [staged]);
  const match = (i: QcInstrument, q: string) =>
    `${i.labwareID ?? ''} ${i.instrumentName ?? ''} ${i.labwareName ?? ''}`.toLowerCase().includes(q);
  const available = useMemo(() => {
    const q = searchL.trim().toLowerCase();
    return options.filter(i => !stagedIds.has(i.instrumentOid) && (!q || match(i, q)));
  }, [options, stagedIds, searchL]);
  const selected = useMemo(() => {
    const q = searchR.trim().toLowerCase();
    return q ? staged.filter(i => match(i, q)) : staged;
  }, [staged, searchR]);
  const addOne = (i: QcInstrument) => setStaged(prev => [...prev, i]);
  const removeOne = (oid: string) => setStaged(prev => prev.filter(s => s.instrumentOid !== oid));
  // Fixed 3-col width for both panels: cbcol via CSS (34px), then 22% / 44% / 34%
  // for InstrumentID / InstrumentName / InstrumentNumber. table-layout:fixed makes
  // these authoritative so long names wrap instead of pushing the layout.
  const Cols = () => (
    <colgroup>
      <col style={{ width: 34 }} />
      <col style={{ width: '22%' }} />
      <col style={{ width: '44%' }} />
      <col style={{ width: '34%' }} />
    </colgroup>
  );
  return (
    <Scrim onClose={onClose} width={980}>
      <div className="mh">🔬 Instruments <span className="x" onClick={onClose}>×</span></div>
      <div className="pb"><div className="dual">
        <div className="dualcol">
          <div className="ds"><input value={searchL} onChange={e => setSearchL(e.target.value)} placeholder="Enter text to search…" /></div>
          <div className="dscroll"><table><Cols />
            <thead><tr><th className="cbcol" /><th>InstrumentID</th><th>InstrumentName</th><th>InstrumentNumber</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="onodata">Loading…</td></tr>
              ) : available.length === 0 ? (
                <tr><td colSpan={4} className="onodata">No matches</td></tr>
              ) : available.map(i => (
                <tr key={i.instrumentOid}>
                  <td className="cbcol"><button className="circ add" onClick={() => addOne(i)}>＋</button></td>
                  <td>{i.labwareID || ''}</td><td>{i.instrumentName || ''}</td><td>{i.labwareName || ''}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <div className="dfoot"><span>{available.length} items</span><span>Page size: 100</span></div>
        </div>
        <div className="dualcol">
          <div className="ds"><input value={searchR} onChange={e => setSearchR(e.target.value)} placeholder="Enter text to search…" /></div>
          <div className="dscroll"><table><Cols />
            <thead><tr><th className="cbcol" /><th>InstrumentID</th><th>InstrumentName</th><th>InstrumentNumber</th></tr></thead>
            <tbody>
              {selected.length === 0 ? (
                <tr><td colSpan={4} className="onodata">Nothing selected</td></tr>
              ) : selected.map(i => (
                <tr key={i.instrumentOid}>
                  <td className="cbcol"><button className="circ rm" onClick={() => removeOne(i.instrumentOid)}>−</button></td>
                  <td>{i.labwareID || ''}</td><td>{i.instrumentName || ''}</td><td>{i.labwareName || ''}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <div className="dfoot"><span>{selected.length} items</span><span>Page size: 100</span></div>
        </div>
      </div></div>
      <div className="mf">
        <button className="btn primary" onClick={() => onCommit(staged)}>OK</button>
        <button className="btn" onClick={onClose}>Cancel</button>
      </div>
    </Scrim>
  );
}

// ReagentPicker — same mockup dual-panel design (#reagPickScrim /
// renderReagPicker), columns matched to the legacy popup's Model.xafml
// ListViews `NPReagents_Available_ListView` / `NPReagents_Selected_ListView`
// (BTLIMS.Web\Model.xafml :33270-33320) — BOTH panels show the IDENTICAL
// 8-column set, same order: ReagentID · ReagentName · PrepType ·
// ExpirationDate · Test · DatePrepared · PreparedBy · DateReceived (a
// `VendorLT` field exists on the underlying `NPReagentQCBatch` DTO but is
// hidden, Index="-1", on both legacy ListViews — left out here too).
//
// Reagent staged rows are a UNION: pre-linked rows (carry an Oid — remove
// action on OK becomes an unlinkReagent) OR newly-picked catalog rows (no Oid
// yet — add action on OK becomes a linkReagent, which clones the catalog into
// a fresh dbo.Reagent). ReagentID is the merge key (unique per catalog row).
type StagedReagent = {
  reagentID:   string;
  reagentName: string | null;
  prepType:    string | null;
  expiryDate:  string | null;
  test:        string | null;
  datePrepared: string | null;
  preparedBy:  string | null;
  dateReceived: string | null;
  linkedOid?:  string;             // present if this row was already linked pre-open
  catalog?:    QcAvailableReagent; // present if this row is a NEW pick from the catalog
};
type PreLinkedReagent = {
  oid?: string;
  reagentID:   string | null;
  reagentName: string | null;
  prepType:    string | null;
  expiryDate:  string | null;
  test?:       string | null;      // not stored on a saved dbo.Reagent row — blank when reopening
  datePrepared?: string | null;
  preparedBy?: string | null;
  dateReceived?: string | null;
};
// Date-filter dropdown above the Available panel — legacy NPReagentDateFilter
// SingleChoiceAction (QCBatchController.Designer.cs :190-209), default "1Y".
// Filters on a row's FilterDate := DateReceived ?? DatePrepared (legacy
// GetDataFromSproc :12523-12532 — DateReceived, checked second, wins when
// both are present); "All" removes the filter entirely (legacy's `else`
// branch in NPReagentDateFilter_SelectedItemChanged just clears the criteria).
const REAGENT_DATE_FILTERS: { id: string; months?: number; years?: number }[] = [
  { id: '1M', months: 1 }, { id: '3M', months: 3 }, { id: '6M', months: 6 },
  { id: '1Y', years: 1 }, { id: '2Y', years: 2 }, { id: '5Y', years: 5 },
  { id: 'All' },
];
function ReagentPicker({ preLinked, testMethodId, onClose, onCommit }:
  { preLinked: PreLinkedReagent[]; testMethodId?: string | null;
    onClose: () => void; onCommit: (final: StagedReagent[]) => void }) {
  const notify = useNotify();
  const [options, setOptions] = useState<QcAvailableReagent[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('1Y');
  const [staged, setStaged] = useState<StagedReagent[]>(
    preLinked
      .filter(p => !!p.reagentID)
      .map(p => ({
        reagentID: p.reagentID!,
        reagentName: p.reagentName,
        prepType: p.prepType,
        expiryDate: p.expiryDate,
        test: p.test ?? null,
        datePrepared: p.datePrepared ?? null,
        preparedBy: p.preparedBy ?? null,
        dateReceived: p.dateReceived ?? null,
        linkedOid: p.oid,
      })));
  const [searchL, setSearchL] = useState('');
  const [searchR, setSearchR] = useState('');
  useEffect(() => {
    // Scoped to the batch's test — legacy passes the TestMethod Oid to its reagent SP
    // (QCBatchController.cs:12802); unscoped, this listed every reagent in the lab.
    qcBatchApi.availableReagents(testMethodId)
      .then(setOptions)
      .catch(() => notify.error('Could not load the reagent catalog.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const stagedIds = useMemo(() => new Set(staged.map(s => s.reagentID)), [staged]);
  const match = (id: string, name: string | null, prep: string | null, q: string) =>
    `${id} ${name ?? ''} ${prep ?? ''}`.toLowerCase().includes(q);
  const dateFilterCutoff = useMemo(() => {
    const opt = REAGENT_DATE_FILTERS.find(o => o.id === dateFilter);
    if (!opt || (!opt.months && !opt.years)) return null; // "All" — no cutoff
    const d = new Date();
    if (opt.months) d.setMonth(d.getMonth() - opt.months);
    if (opt.years) d.setFullYear(d.getFullYear() - opt.years);
    return d;
  }, [dateFilter]);
  const available = useMemo(() => {
    const q = searchL.trim().toLowerCase();
    return options.filter(o => {
      if (!o.reagentID || stagedIds.has(o.reagentID)) return false;
      if (dateFilterCutoff) {
        const raw = o.dateReceived ?? o.datePrepared;
        if (!raw) return false;
        const d = new Date(raw);
        if (Number.isNaN(d.getTime()) || d < dateFilterCutoff) return false;
      }
      return !q || match(o.reagentID, o.reagentName, o.prepType, q);
    });
  }, [options, stagedIds, searchL, dateFilterCutoff]);
  const selected = useMemo(() => {
    const q = searchR.trim().toLowerCase();
    return q ? staged.filter(s => match(s.reagentID, s.reagentName, s.prepType, q)) : staged;
  }, [staged, searchR]);
  const addOne = (o: QcAvailableReagent) => {
    if (!o.reagentID) return;
    setStaged(prev => [...prev, {
      reagentID: o.reagentID!,
      reagentName: o.reagentName,
      prepType: o.prepType,
      expiryDate: o.expiryDate,
      test: o.test,
      datePrepared: o.datePrepared,
      preparedBy: o.preparedBy,
      dateReceived: o.dateReceived,
      catalog: o,
    }]);
  };
  const removeOne = (reagentID: string) => setStaged(prev => prev.filter(s => s.reagentID !== reagentID));
  // Same 8-column layout on both panels (legacy parity — see comment above the
  // StagedReagent type). Fixed px widths (not %) matching the live legacy
  // popup's own generous column widths (user-supplied screenshot, 2026-07-06).
  // table-layout:fixed with the table itself left at width:auto still SHRINKS
  // the colgroup's widths proportionally to fit the panel (confirmed live —
  // headers wrapped mid-word instead of honoring these px values) — a <table>
  // only overflows its container, and .dscroll's overflow-x:auto only shows a
  // scrollbar, once the table has an explicit width GREATER than the
  // container. So the table's own width= is set below to the literal sum of
  // these columns, not left to 'auto'.
  const REAGENT_COL_WIDTHS = [44, 110, 150, 100, 110, 120, 110, 100, 110];
  const reagentTableWidth = REAGENT_COL_WIDTHS.reduce((a, b) => a + b, 0);
  const Cols = () => (
    <colgroup>
      {REAGENT_COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
    </colgroup>
  );
  return (
    <Scrim onClose={onClose} width={1180}>
      <div className="mh">📦 Add Reagents <span className="x" onClick={onClose}>×</span></div>
      <div className="pb">
        <div className="reag-datefilter">
          <label htmlFor="reag-datefilter-sel">Date Filter</label>
          <select id="reag-datefilter-sel" value={dateFilter} onChange={e => setDateFilter(e.target.value)}>
            {REAGENT_DATE_FILTERS.map(o => <option key={o.id} value={o.id}>{o.id}</option>)}
          </select>
        </div>
        <div className="dual">
        <div className="dualcol">
          <div className="ds"><input value={searchL} onChange={e => setSearchL(e.target.value)} placeholder="Enter text to search…" /></div>
          <div className="dscroll wide"><table className="wide" style={{ width: reagentTableWidth }}><Cols />
            <thead><tr><th className="cbcol" /><th>ReagentID</th><th>ReagentName</th><th>PrepType</th><th>ExpirationDate</th><th>Test</th><th>DatePrepared</th><th>PreparedBy</th><th>DateReceived</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="onodata">Loading…</td></tr>
              ) : available.length === 0 ? (
                <tr><td colSpan={9} className="onodata">No matches</td></tr>
              ) : available.map(o => (
                <tr key={o.reagentID}>
                  <td className="cbcol"><button className="circ add" onClick={() => addOne(o)}>＋</button></td>
                  <td>{o.reagentID}</td><td>{o.reagentName}</td><td>{o.prepType || ''}</td><td>{fmtDate(o.expiryDate)}</td>
                  <td>{o.test || ''}</td><td>{fmtDate(o.datePrepared)}</td><td>{o.preparedBy || ''}</td><td>{fmtDate(o.dateReceived)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <div className="dfoot"><span>{available.length} items</span><span>Page size: 20</span></div>
        </div>
        <div className="dualcol">
          <div className="ds"><input value={searchR} onChange={e => setSearchR(e.target.value)} placeholder="Enter text to search…" /></div>
          <div className="dscroll wide"><table className="wide" style={{ width: reagentTableWidth }}><Cols />
            <thead><tr><th className="cbcol" /><th>ReagentID</th><th>ReagentName</th><th>PrepType</th><th>ExpirationDate</th><th>Test</th><th>DatePrepared</th><th>PreparedBy</th><th>DateReceived</th></tr></thead>
            <tbody>
              {selected.length === 0 ? (
                <tr><td colSpan={9} className="onodata">Nothing selected</td></tr>
              ) : selected.map(s => (
                <tr key={s.reagentID}>
                  <td className="cbcol"><button className="circ rm" onClick={() => removeOne(s.reagentID)}>−</button></td>
                  <td>{s.reagentID}</td><td>{s.reagentName}</td><td>{s.prepType || ''}</td><td>{fmtDate(s.expiryDate)}</td>
                  <td>{s.test || ''}</td><td>{fmtDate(s.datePrepared)}</td><td>{s.preparedBy || ''}</td><td>{fmtDate(s.dateReceived)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <div className="dfoot"><span>{selected.length} items</span><span>Page size: 20</span></div>
        </div>
      </div></div>
      <div className="mf">
        <button className="btn primary" onClick={() => onCommit(staged)}>OK</button>
        <button className="btn" onClick={onClose}>Cancel</button>
      </div>
    </Scrim>
  );
}
// Add-Samples picker for a SAVED batch — legacy AddQCBatchSample_Execute parity
// (a distinct pool from the initial Load: still-pending samples for the
// batch's own test method, across ALL jobs).
function AddSamplePicker({ abOid, onClose, onCommit }:
  { abOid: string; onClose: () => void; onCommit: (sel: QcSample[]) => void }) {
  const notify = useNotify();
  const [options, setOptions] = useState<QcSample[]>([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  useEffect(() => {
    qcBatchApi.addSampleCandidates(abOid)
      .then(setOptions)
      .catch(() => notify.error('Could not load candidate samples.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abOid]);
  const toggle = (id: string) => setSel(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const q = search.trim().toLowerCase();
  const filtered = q ? options.filter(o => (o.sysSampleCode ?? '').toLowerCase().includes(q)) : options;
  return (
    <Scrim onClose={onClose} width={760}>
      <div className="mh">➕ Add Samples <span className="x" onClick={onClose}>×</span></div>
      <div className="pb">
        <div className="list-search" style={{ marginBottom: 8 }}>
          <i className="ti ti-search" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" />
          {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
        </div>
        <div className="dual">
        <div className="dualcol" style={{ width: '100%' }}>
          <div className="dscroll">
            {loading && <div className="onodata" style={{ padding: 20 }}>Loading…</div>}
            {!loading && filtered.length === 0 && <div className="onodata" style={{ padding: 20 }}>No pending samples available for this test.</div>}
            {filtered.map(o => (
              <div className="ditem" key={o.sampleLogInOid} onClick={() => toggle(o.sampleLogInOid)}>
                <input type="checkbox" className="chk" readOnly checked={sel.has(o.sampleLogInOid)} />
                <span>{o.sysSampleCode}{o.requiresPrep ? ' — prep required' : ''}</span>
              </div>
            ))}
          </div>
          <div className="dfoot"><span>Selected ({sel.size})</span><span>Page size: 100</span></div>
        </div>
      </div></div>
      <div className="mf">
        <button className="btn primary" onClick={() => onCommit(options.filter(o => sel.has(o.sampleLogInOid)))}>OK</button>
        <button className="btn" onClick={onClose}>Cancel</button>
      </div>
    </Scrim>
  );
}
function Scrim({ onClose, width, children }: { onClose: () => void; width: number; children: ReactNode }) {
  return (
    <div className="qcb-scrim" onClick={onClose}>
      <div className="pickmodal" style={{ width }} onClick={e => e.stopPropagation()}>{children}</div>
    </div>
  );
}

// Short date for picker cells via the shared helper (§18.6 — never inline).
function fmtDate(v: string | null): string {
  return v ? fmtLocalDate(v) : '';
}
